import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { getSupabaseClient } from '../supabaseClient';
import { useBusinessType } from '../context/BusinessContext';
import { useMediaQuery } from '../hooks/use-media-query';
import { useIntegrationsV2 } from '../hooks/useIntegrationsV2';

import { toast } from "sonner";
import BrainActionModal from '../brain/BrainActionModal';
import SimpleAnswerModal from '../brain/SimpleAnswerModal';
import { ComplexAnswerSettingsModal } from '../components/complex-answer/ComplexAnswerSettingsModal';
import CallActionSettingsModal from '../components/call-actions/CallActionSettingsModal';
import WorkflowModal from '../components/workflow/WorkflowModal';
import BrainSidebar from '../components/BrainSidebar';
import BrainMainContent from '../components/BrainMainContent';

import BrainChatInterface from '../components/brain/BrainChatInterface';
import { motion, AnimatePresence } from 'framer-motion';
import { FolderOpen, Plus, AndroidLogo, CaretLeft, CaretRight, ChartBar, X, ShieldWarning } from '@phosphor-icons/react';
import { CircleNotch } from '@phosphor-icons/react';
import OrderPathwayModal from '../components/order-pathways/OrderPathwayModal';
import NewCategoryModal from '../brain/NewCategoryModal';
import { cn } from '../lib/utils';
import { API_BASE_URL, endpoints } from '../config/api';
import { runAutomaticConflictDetection, getExistingConflicts } from '../utils/conflictDetection';
import { refreshBrainSettings } from '../hooks/usePageContext';
import LocationVariantsModal from '../components/LocationVariantsModal';
import PostCallActionModal from '../components/PostCallActionModal';
import MixedMessagesModal from '../components/MixedMessagesModal';
import Analysis from './Analysis';
import { cleanupCallsAnalysisOnboarding } from '../utils/callsAnalysisCleanup';
import TutorialOverlay from '../components/brain/TutorialOverlay';
import TutorialChaptersModal from '../components/brain/TutorialChaptersModal';
import AITestModal from '../components/brain/AITestModal';
import ActivationConditionsPanel from '../components/brain/ActivationConditionsPanel';



const Brain = () => {
  const { businessType } = useBusinessType();
  const supabase = getSupabaseClient(businessType);
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Mobile detection
  const isMobile = useMediaQuery("(max-width: 640px)");
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  // State management
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [locations, setLocations] = useState([]);
  // REMOVED: const [integrations, setIntegrations] = useState([]);

  // V2 Integration Management - REPLACES old integration state
  const {
    integrationsV2,
    isLoadingV2,
    connectIntegrationV2,
    disconnectIntegrationV2,
    refreshIntegrationsV2
  } = useIntegrationsV2();

  // Complete integrations loading operation when V2 integrations finish loading
  useEffect(() => {
    if (!isLoadingV2) {
      completeLoadingOperation('integrations');
    }
  }, [isLoadingV2]);

  // Stub functions for V2 integration handlers that aren't implemented yet
  const updateIntegrationCredentialsV2 = async (integrationId, credentials) => {
    // TODO: Implement credential updates through the V2 service
    toast.info('Credential updates not yet implemented');
  };

  const triggerSyncV2 = async (integrationId) => {
    // TODO: Implement sync triggering through the V2 service
    toast.info('Manual sync not yet implemented');
  };

  const toggleIntegrationStatusV2 = async (integrationId, enabled) => {
    // TODO: Implement status toggling through the V2 service
    toast.info('Status toggling not yet implemented');
  };

  // Post Call Actions state
  const [postCallActions, setPostCallActions] = useState([]);
  const [expandedPostCallActions, setExpandedPostCallActions] = useState(new Set());

  // Clarification Questions state
  const [clarificationQuestions, setClarificationQuestions] = useState([]);

  // Simple loading completion tracker - no complex state manager needed
  const [completedOperations, setCompletedOperations] = useState(new Set());
  const REQUIRED_OPERATIONS = ['categories', 'integrations', 'postCallActions', 'account', 'multilingualSettings', 'clarificationQuestions'];



  // Template mode detection and coverage analysis - adapted from Analysis.js
  const [isTemplateMode, setIsTemplateMode] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [accountId, setAccountId] = useState(null);

  // AI Coverage state for sidebar button
  const [coveragePercentage, setCoveragePercentage] = useState(0);
  const [isLoadingCoverage, setIsLoadingCoverage] = useState(false);

  // Add analysis panel state
  const [isAnalysisPanelOpen, setIsAnalysisPanelOpen] = useState(false);

  // Add tutorial state
  const [isTutorialActive, setIsTutorialActive] = useState(false);
  const [isTutorialChaptersModalOpen, setIsTutorialChaptersModalOpen] = useState(false);
  const [currentTutorialChapter, setCurrentTutorialChapter] = useState(null);

  // Auto-start tutorial for new users from onboarding
  useEffect(() => {
    const shouldStartTutorial = localStorage.getItem('startBrainTutorial');
    if (shouldStartTutorial === 'true') {
      localStorage.removeItem('startBrainTutorial');

      // Get Chapter 1 configuration
      const chapter1 = {
        id: 'chapter-1',
        title: 'Meeting Your Laundry AI Brain',
        description: 'Get familiar with the interface and understand how your AI organizes knowledge'
      };

      setCurrentTutorialChapter(chapter1);
      setIsTutorialActive(true);
    }
  }, []);

  const handleStartTutorialChapter = (chapter) => {
    setCurrentTutorialChapter(chapter);
    setIsTutorialActive(true);
  };

  const handleCompleteTutorialChapter = () => {
    setIsTutorialActive(false);
    setCurrentTutorialChapter(null);
    // Optionally reopen chapters modal to show progress
    setTimeout(() => setIsTutorialChaptersModalOpen(true), 500);
  };

  const handleSkipTutorialChapter = () => {
    setIsTutorialActive(false);
    setCurrentTutorialChapter(null);
  };

  // Toggle function for Analysis panel
  const handleToggleAnalysisPanel = () => {
    setIsAnalysisPanelOpen(!isAnalysisPanelOpen);
  };

  // Handle edit setting from Analysis - select the setting and keep Analysis panel open
  const handleAnalysisEditSetting = (inquiry) => {
    // Find the actual setting in our categories
    const setting = categories
      .flatMap(cat => cat.settings)
      .find(s => s.id === inquiry.brain_setting_id);

    if (setting) {
      // Find the category for context
      const category = categories.find(cat => 
        cat.settings && cat.settings.some(s => s.id === setting.id)
      );

      // Select the setting (this will trigger inline editing)
      setSelectedItem({
        ...setting,
        type: 'setting',
        category_name: category?.name || 'Unknown Category'
      });

      // Keep Analysis panel open - user can see both Analysis and editing
    }
  };

  // Handle create setting from Analysis - open action modal and keep Analysis panel open
  const handleAnalysisCreateSetting = (inquiry) => {
    // Pre-select the category if we know it
    setPreSelectedCategoryId(inquiry.category_id || null);

    // Keep Analysis panel open - user can see both Analysis and creation

    // Open the action modal
    setIsActionModalOpen(true);
  };

  // Toggle function for Mixed Messages modal
  const handleToggleMixedMessagesModal = () => {
    setIsMixedMessagesModalOpen(!isMixedMessagesModalOpen);
  };

  // Toggle function for Test Modal
  const handleToggleTestModal = () => {
    setIsTestModalOpen(!isTestModalOpen);
  };

  // Function to fetch real coverage data - works for both template and real data modes
  const fetchCoverageData = async () => {
    if (!accountId) {
      setCoveragePercentage(0);
      return;
    }

    try {
      setIsLoadingCoverage(true);

      if (isTemplateMode) {
        // TEMPLATE MODE: Use calls_analysis_onboarding table

        // Get account service types first
        const { data: serviceTypesData, error: serviceTypesError } = await supabase
          .from('accounts_services_types')
          .select(`
            system_service_types!inner(
              name,
              display_order
            )
          `)
          .eq('account_id', accountId);

        if (serviceTypesError) throw serviceTypesError;

        const accountServiceTypes = serviceTypesData
          .sort((a, b) => a.system_service_types.display_order - b.system_service_types.display_order)
          .map(item => item.system_service_types.name);

        if (accountServiceTypes.length === 0) {
          setCoveragePercentage(0);
          return;
        }

        // Fetch template inquiries
        const { data: templateData, error: templateError } = await supabase
          .from('calls_analysis_template_inquiries')
          .select(`
            id,
            inquiry,
            type,
            priority,
            created_at,
            category_id,
            service_type,
            calls_analysis_categories!inner(
              id,
              category_name,
              friendly_name
            )
          `)
          .eq('business_type', businessType)
          .in('service_type', accountServiceTypes)
          .order('priority', { ascending: true });

        if (templateError) throw templateError;

        // Check coverage data
        const { data: existingCoverage, error: coverageError } = await supabase
          .from('calls_analysis_onboarding')
          .select(`
            template_inquiry_id,
            covered,
            brain_setting_id
          `)
          .eq('account_id', accountId);

        if (coverageError) throw coverageError;

        // Create coverage map
        const coverageMap = new Map();
        if (existingCoverage) {
          existingCoverage.forEach(item => {
            coverageMap.set(item.template_inquiry_id, {
              covered: item.covered
            });
          });
        }

        // Transform data with existing coverage
        const transformedTemplate = templateData.map(item => {
          const coverage = coverageMap.get(item.id);

          return {
            id: item.id,
            covered: coverage?.covered || false
          };
        });

        // Calculate coverage percentage
        const total = transformedTemplate.length;
        const covered = transformedTemplate.filter(item => item.covered === true).length;
        const coveragePercentage = total > 0 ? Math.round((covered / total) * 100) : 0;

        setCoveragePercentage(coveragePercentage);

      } else {
        // REAL DATA MODE: Use calls_analysis table (same logic as Analysis.js)

        const { data: analysisData, error: analysisError } = await supabase
          .from('calls_analysis')
          .select(`
            id,
            call_id,
            type,
            inquiry,
            created_at,
            category_id,
            calls_analysis_coverage!left (
              covered,
              brain_settings (
                id,
                name,
                type,
                category_id,
                brain_categories (name),
                brain_simple_actions (action_type)
              )
            ),
            calls!inner (created_at, call_category)
          `)
          .eq('account_id', accountId)
          .order('created_at', { ascending: false });

        if (analysisError) throw analysisError;

        // Transform analysis data (same as Analysis.js)
        const transformedAnalysis = analysisData.map(item => ({
          id: item.id,
          call_id: item.call_id,
          type: item.type,
          inquiry: item.inquiry,
          created_at: item.created_at,
          category_id: item.category_id,
          covered: item.calls_analysis_coverage?.covered,
          brain_setting_id: item.calls_analysis_coverage?.brain_settings?.id,
          brain_setting_name: item.calls_analysis_coverage?.brain_settings?.name,
          brain_setting_type: item.calls_analysis_coverage?.brain_settings?.type,
          brain_category_name: item.calls_analysis_coverage?.brain_settings?.brain_categories?.name,
          action_type: item.calls_analysis_coverage?.brain_settings?.brain_simple_actions?.[0]?.action_type,
          call_created_at: item.calls?.created_at,
          call_category: item.calls?.call_category
        }));

        // Calculate coverage percentage (same logic as Analysis.js)
        const total = transformedAnalysis.length;
        const covered = transformedAnalysis.filter(item => item.covered === true).length;
        const coveragePercentage = total > 0 ? Math.round((covered / total) * 100) : 0;

        setCoveragePercentage(coveragePercentage);
      }

    } catch (error) {
      console.error('[Brain.js] Error fetching coverage data:', error);
      setCoveragePercentage(0);
    } finally {
      setIsLoadingCoverage(false);
    }
  };

  // Simple coverage effect: fetch when accountId and mode are determined
  useEffect(() => {
    // Only proceed if we have the necessary data and mode is determined
    if (accountId && isTemplateMode !== undefined && businessType) {
      console.log('[Brain.js] Account and mode detected, fetching coverage data');
      fetchCoverageData();
    } else {
      // Reset coverage if conditions aren't met
      setCoveragePercentage(0);
      setIsLoadingCoverage(false);
    }
  }, [accountId, isTemplateMode, businessType]); // Works for both template and real data modes

  // Remove the old separate useEffects that were causing multiple loads
  // useEffect(() => {
  //   if (accountId && isTemplateMode) {
  //     fetchCoverageData();
  //   } else {
  //     setCoveragePercentage(0);
  //   }
  // }, [accountId, isTemplateMode, businessType]);

  // Remove the debounced categories effect that was causing extra loads
  // useEffect(() => {
  //   if (accountId && isTemplateMode) {
  //     const timeoutId = setTimeout(() => {
  //       fetchCoverageData();
  //     }, 1000); // Debounce to avoid excessive API calls
  //     
  //     return () => clearTimeout(timeoutId);
  //   }
  // }, [categories]);

  // Accordion state management - lifted up to preserve across category updates
  const [expandedCategories, setExpandedCategories] = useState(() => {
    const initialExpanded = new Set();
    return initialExpanded;
  });
  const [expandedSettings, setExpandedSettings] = useState(new Set());

  // Initialize expanded categories when categories first load
  useEffect(() => {
    if (categories.length > 0 && expandedCategories.size === 0) {
      const initialExpanded = new Set();
      categories.forEach(category => {
        if (category.settings && category.settings.length > 0) {
          initialExpanded.add(category.id);
        }
      });
      setExpandedCategories(initialExpanded);
    }
  }, [categories.length]); // Only run when categories first load

  // Auto-manage category expansion: expand categories with settings, collapse empty ones
  useEffect(() => {
    if (categories.length > 0) {
      setExpandedCategories(prevExpanded => {
        const newExpanded = new Set();

        // Auto-expand categories that have settings
        categories.forEach(category => {
          if (category.settings && category.settings.length > 0) {
            newExpanded.add(category.id);
          }
        });

        // Only update if there's an actual change (optimization)
        if (newExpanded.size !== prevExpanded.size || 
            ![...newExpanded].every(id => prevExpanded.has(id))) {
          return newExpanded;
        }

        return prevExpanded;
      });
    }
  }, [categories]); // Run whenever categories change

  // Conflict detection state
  const [conflicts, setConflicts] = useState([]);
  const [isLoadingConflicts, setIsLoadingConflicts] = useState(false);
  const [isProcessingConflicts, setIsProcessingConflicts] = useState(false);
  const [currentConflictIndex, setCurrentConflictIndex] = useState(() => {
    // Initialize from URL parameter if present, with proper error handling
    try {
      const conflictParam = searchParams.get('conflict');
      const parsed = conflictParam ? parseInt(conflictParam, 10) : 0;
      return !isNaN(parsed) && parsed >= 0 ? parsed : 0;
    } catch (error) {
      console.warn('Error parsing conflict parameter from URL:', error);
      return 0;
    }
  });

  // Layout state
  const [activeSidePanel, setActiveSidePanel] = useState(null);

  // Activation conditions state
  const [isConditionsModalOpen, setIsConditionsModalOpen] = useState(false);
  const [settingForConditions, setSettingForConditions] = useState(null);

  // Filter for affected settings only
  const [showOnlyAffectedSettings, setShowOnlyAffectedSettings] = useState(false);

  const toggleAffectedSettingsFilter = () => {
    setShowOnlyAffectedSettings(prev => !prev);
  };

  // Get affected setting IDs from conflicts
  const getAffectedSettingIds = () => {
    const affectedIds = new Set();

    // When modal is open, only show settings from current conflict
    if (activeSidePanel === 'conflicts' && conflicts.length > 0 && conflicts[currentConflictIndex]) {
      const currentConflict = conflicts[currentConflictIndex];
      if (currentConflict.settings && currentConflict.settings.length > 0) {
        currentConflict.settings.forEach(setting => {
          if (setting.id) {
            affectedIds.add(setting.id);
          }
        });
      }
      return affectedIds;
    }

    // When modal is closed, show all conflicted settings
    conflicts.forEach(conflict => {
      if (conflict.settings && conflict.settings.length > 0) {
        conflict.settings.forEach(setting => {
          if (setting.id) {
            affectedIds.add(setting.id);
          }
        });
      }
    });
    return affectedIds;
  };

  // NEW: Open Mixed Messages Modal for specific conflict containing a setting
  const handleOpenConflictForSetting = (settingId) => {
    console.log('🔍 Finding conflict for setting:', settingId);
    console.log('🔍 Available conflicts:', conflicts);

    // Find which conflict contains this setting
    const conflictIndex = conflicts.findIndex(conflict => {
      console.log('🔍 Checking conflict:', conflict);

      // Check if conflict has settings array and look for the setting ID
      if (conflict.settings && Array.isArray(conflict.settings)) {
        const hasSettingId = conflict.settings.some(setting => {
          console.log('🔍 Checking setting in conflict:', setting);
          return setting.id === settingId;
        });
        console.log('🔍 Conflict contains setting:', hasSettingId);
        return hasSettingId;
      }

      // Also check if conflict has affected_settings_ids array (fallback)
      if (conflict.affected_settings_ids && Array.isArray(conflict.affected_settings_ids)) {
        return conflict.affected_settings_ids.includes(settingId);
      }

      return false;
    });

    console.log('🔍 Found conflict index:', conflictIndex);

    if (conflictIndex !== -1) {
      // Update URL parameter
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('conflict', conflictIndex.toString());
      setSearchParams(newSearchParams, { replace: true });

      // Set the conflict index and open the modal
      setCurrentConflictIndex(conflictIndex);
      handleSidePanelToggle('conflicts');

      console.log('🔍 Successfully opened conflict modal for index:', conflictIndex);
    } else {
      console.warn('🔍 No conflict found containing setting:', settingId);
      // Fallback: just open the modal to the first conflict
      handleSidePanelToggle('conflicts');
    }
  };

  // NEW: Update URL when conflict index changes
  const updateConflictIndexInUrl = (index) => {
    // Guard against undefined or invalid index values during initialization
    if (typeof index !== 'number' || index < 0) {
      return;
    }

    try {
      const newSearchParams = new URLSearchParams(searchParams);
      if (index > 0) {
        newSearchParams.set('conflict', index.toString());
      } else {
        newSearchParams.delete('conflict'); // Remove parameter for index 0
      }
      setSearchParams(newSearchParams, { replace: true });
    } catch (error) {
      console.warn('Error updating conflict URL parameter:', error);
    }
  };

  // Reset conflict index when conflicts change and validate URL parameter
  useEffect(() => {
    const urlConflictIndex = parseInt(searchParams.get('conflict') || '0', 10);
    const validIndex = Math.max(0, Math.min(urlConflictIndex, conflicts.length - 1));

    // Only update if URL parameter is invalid or if we have conflicts and need to validate bounds
    if (urlConflictIndex >= conflicts.length && conflicts.length > 0) {
      setCurrentConflictIndex(validIndex);
      updateConflictIndexInUrl(validIndex);
    } else if (conflicts.length === 0) {
      // Reset to 0 when no conflicts
      setCurrentConflictIndex(0);
    } else if (urlConflictIndex !== validIndex) {
      // Update to valid index if URL param is different
      setCurrentConflictIndex(validIndex);
    }
  }, [conflicts.length, searchParams]);

  // Sync conflict index changes to URL (only after initial mount)
  useEffect(() => {
    // Add a guard to prevent running on initial mount before currentConflictIndex is stable
    const timeoutId = setTimeout(() => {
      updateConflictIndexInUrl(currentConflictIndex);
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [currentConflictIndex]);

  // Auto-enable filter when mixed messages modal opens
  useEffect(() => {
    if (activeSidePanel === 'conflicts' && conflicts.length > 0) {
      setShowOnlyAffectedSettings(true);
    } else if (activeSidePanel !== 'conflicts') {
      setShowOnlyAffectedSettings(false);
    }
  }, [activeSidePanel, conflicts.length]);

  // Modal states
  const [isActionModalOpen, setIsActionModalOpen] = useState(false);
  const [isOrderPathwayModalOpen, setIsOrderPathwayModalOpen] = useState(false);
  const [isNewCategoryModalOpen, setIsNewCategoryModalOpen] = useState(false);
  const [categoryCreationContext, setCategoryCreationContext] = useState(null); // Track who requested category creation
  const [newlyCreatedCategory, setNewlyCreatedCategory] = useState(null); // Category to auto-select in SimpleAnswerModal
  const [isLocationVariantsModalOpen, setIsLocationVariantsModalOpen] = useState(false);
  const [selectedSettingForLocations, setSelectedSettingForLocations] = useState(null);
  const [selectedIntegrationForDetails, setSelectedIntegrationForDetails] = useState(null);
  const [isPostCallActionModalOpen, setIsPostCallActionModalOpen] = useState(false);
  const [editingPostCallAction, setEditingPostCallAction] = useState(null);

  // Current setting to edit
  const [currentSetting, setCurrentSetting] = useState(null);
  const [editingPathwayId, setEditingPathwayId] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null); // State for category being edited
  const [preSelectedCategoryId, setPreSelectedCategoryId] = useState(null); // State for pre-selecting category when adding new items

  // Mobile header state
  const [isHeaderVisible, setIsHeaderVisible] = useState(false);
  const [isSettingsDropdownOpen, setIsSettingsDropdownOpen] = useState(false);

  // Global multilingual settings state
  const [globalMultilingualSettings, setGlobalMultilingualSettings] = useState({ multilingual: false });

  // Callback for updating conflicts from automatic detection
  const updateConflicts = (newConflicts) => {
    setConflicts(newConflicts);
  };

  // Helper function to add a newly created setting to local state without full refresh
  const addNewSettingToCategories = (newSettingData) => {
    setCategories(prevCategories => {
      return prevCategories.map(category => {
        if (category.id === newSettingData.category_id) {
          // Ensure the new setting has the same structure as settings loaded from fetchCategories
          const settingWithOriginalType = {
            ...newSettingData,
            // Try multiple possible ID fields, or generate a temporary one
            id: newSettingData.id || newSettingData.settings_id || newSettingData.setting_id || `temp-${Date.now()}-${Math.random()}`,
            original_type: newSettingData.type || newSettingData.original_type, // BrainMainContent expects original_type
            // Preserve action_type for simple actions (needed for proper display in BrainSidebar)
            action_type: newSettingData.action_type,
            locationVariants: [] // Initialize empty location variants
          };

          // Debug log if we had to use a temporary ID
          if (!newSettingData.id && !newSettingData.settings_id && !newSettingData.setting_id) {
            console.log('Using temporary ID for new setting:', newSettingData);
          }

          // Add the new setting to this category
          return {
            ...category,
            settings: [...(category.settings || []), settingWithOriginalType]
          };
        }
        return category;
      });
    });
  };

  // Ref to access BrainSidebar functions
  const brainSidebarRef = useRef();

  // Helper function to update multilingual settings locally
  const updateMultilingualSettings = (settings) => {
    setGlobalMultilingualSettings(settings);
  };

  // Helper function to update a setting in local state
  const updateSettingInCategories = (settingId, changes) => {
    setCategories(prevCategories => {
      // First, find the current setting and its category
      let currentSetting = null;
      let currentCategoryId = null;

      for (const category of prevCategories) {
        if (category.settings && category.settings.length > 0) {
          const foundSetting = category.settings.find(setting => setting.id === settingId);
          if (foundSetting) {
            currentSetting = foundSetting;
            currentCategoryId = category.id;
            break;
          }
        }
      }

      if (!currentSetting) {
        console.warn('Setting not found for update:', settingId);
        return prevCategories;
      }

      // Check if category is changing
      const isCategoryChanging = changes.category_id && changes.category_id !== currentCategoryId;

      return prevCategories.map(category => {
        // If category is changing, handle remove from old and add to new
        if (isCategoryChanging) {
          if (category.id === currentCategoryId) {
            // Remove from current category
            return {
              ...category,
              settings: category.settings ? category.settings.filter(setting => setting.id !== settingId) : []
            };
          } else if (category.id === changes.category_id) {
            // Add to new category with updated data
            const updatedSetting = { ...currentSetting };

            // Apply all changes
            Object.keys(changes).forEach(field => {
              const value = changes[field];

              // Map API fields to local state fields
              switch (field) {
                case 'content':
                  // For simple answers, content maps to both content and answer
                  updatedSetting.content = value;
                  updatedSetting.answer = value;
                  // Also update in fullData if it exists
                  if (updatedSetting.fullData) {
                    updatedSetting.fullData.answer = value;
                  }
                  // Update answer_content for conflict detection
                  updatedSetting.answer_content = value;
                  break;
                case 'answer':
                  // For simple answers, answer maps to both content and answer
                  updatedSetting.content = value;
                  updatedSetting.answer = value;
                  // Also update in fullData if it exists
                  if (updatedSetting.fullData) {
                    updatedSetting.fullData.answer = value;
                  }
                  // Update answer_content for conflict detection
                  updatedSetting.answer_content = value;
                  break;
                case 'name':
                  updatedSetting.name = value;
                  // Also update in fullData if it exists
                  if (updatedSetting.fullData) {
                    updatedSetting.fullData.name = value;
                    updatedSetting.fullData.question = value;
                  }
                  // Update answer_title for conflict detection
                  updatedSetting.answer_title = value;
                  break;
                default:
                  // Direct mapping for most fields
                  updatedSetting[field] = value;
              }
            });

            return {
              ...category,
              settings: [...(category.settings || []), updatedSetting]
            };
          }
        }

        // No category change - update in place
        if (category.settings && category.settings.length > 0) {
          return {
            ...category,
            settings: category.settings.map(setting => {
              if (setting.id === settingId) {
                // Merge the changes into the existing setting
                // Handle special field mappings for different setting types
                const updatedSetting = { ...setting };

                // Apply all changes
                Object.keys(changes).forEach(field => {
                  const value = changes[field];

                  // Map API fields to local state fields
                  switch (field) {
                    case 'content':
                      // For simple answers, content maps to both content and answer
                      updatedSetting.content = value;
                      updatedSetting.answer = value;
                      // Also update in fullData if it exists
                      if (updatedSetting.fullData) {
                        updatedSetting.fullData.answer = value;
                      }
                      // Update answer_content for conflict detection
                      updatedSetting.answer_content = value;
                      break;
                    case 'answer':
                      // For simple answers, answer maps to both content and answer
                      updatedSetting.content = value;
                      updatedSetting.answer = value;
                      // Also update in fullData if it exists
                      if (updatedSetting.fullData) {
                        updatedSetting.fullData.answer = value;
                      }
                      // Update answer_content for conflict detection
                      updatedSetting.answer_content = value;
                      break;
                    case 'name':
                      updatedSetting.name = value;
                      // Also update in fullData if it exists
                      if (updatedSetting.fullData) {
                        updatedSetting.fullData.name = value;
                        updatedSetting.fullData.question = value;
                      }
                      // Update answer_title for conflict detection
                      updatedSetting.answer_title = value;
                      break;
                    default:
                      // Direct mapping for most fields
                      updatedSetting[field] = value;
                  }
                });

                return updatedSetting;
              }
              return setting;
            })
          };
        }
        return category;
      });
    });
  };

  // Helper function to remove a deleted setting from local state without full refresh
  const removeSettingFromCategories = (settingId) => {
    setCategories(prevCategories => {
      return prevCategories.map(category => {
        if (category.settings && category.settings.length > 0) {
          // Remove the setting from this category's settings
          return {
            ...category,
            settings: category.settings.filter(setting => setting.id !== settingId)
          };
        }
        return category;
      });
    });
  };

  // Single initialization effect - starts all operations together to prevent animation restarts
  useEffect(() => {
    const initializeEverything = async () => {
      try {
        // Start all operations in parallel - no individual loading states
        await Promise.all([
          fetchCategories(),
          fetchPostCallActions(),
          fetchClarificationQuestions(),
          initializeAccountData(),
          fetchMultilingualSettings()
        ]);

        // Load conflicts after main data is ready (don't block loading for this)
        loadExistingConflicts();

      } catch (error) {
        console.error('Error during initialization:', error);
        setError('Failed to load brain data. Please try again.');
        // Ensure loading stops even on error
        setIsLoading(false);
      }
    };

    initializeEverything();
  }, []);

  // Initialize account data helper
  const initializeAccountData = async () => {
    try {
      const { getUserAccountId } = await import('../utils/accountUtils');
      const { accountId: userAccountId, error: accountIdError } = await getUserAccountId();
      if (accountIdError || !userAccountId) {
        console.error('Failed to get account ID:', accountIdError);
        return;
      }

      setAccountId(userAccountId);

      // Detect template mode (same logic as Analysis.js)
      await detectTemplateMode(userAccountId);
    } catch (error) {
      console.error('Error initializing account:', error);
    } finally {
      // Use the loading manager to mark account initialization as complete
      completeLoadingOperation('account');
    }
  };

  // Template mode detection - same logic as Analysis.js (lines 1529-1550)
  const detectTemplateMode = async (userAccountId) => {
    if (!userAccountId) return;

    try {
      // Count total rows in calls_analysis for this account
      const { count, error } = await supabase
        .from('calls_analysis')
        .select('*', { count: 'exact', head: true })
        .eq('account_id', userAccountId);

      if (error) throw error;

      // Switch to real data mode only when account has 100+ analysis records
      const hasEnoughData = count >= 100;
      setIsTemplateMode(!hasEnoughData);

    } catch (err) {
      console.error('Error detecting template mode:', err);
      setIsTemplateMode(true); // Default to template mode on error
    }
  };

  // Coverage analysis refresh function - adapted from Analysis.js fetchTemplateAnalysisData
  // REMOVED: refreshCoverageAnalysis - we now analyze individual settings only
  // Coverage is calculated from individual setting analyses via analyzeSettingCoverage()

  // NEW: Analyze coverage impact for a specific setting change
  const analyzeSettingCoverage = async (settingsId) => {
    try {
      // Skip if no account ID or settings ID
      if (!accountId || !settingsId) return;

      console.log('[Brain.js] Analyzing coverage impact for setting:', settingsId);

      const response = await fetch(`${API_BASE_URL}${endpoints.coverageSetting}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          account_id: accountId,
          settings_id: settingsId
        })
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          console.log('[Brain.js] Setting coverage analysis completed:', {
            covered: result.metadata?.covered || 0,
            notCovered: result.metadata?.notCovered || 0
          });

          // Refresh the coverage percentage display
          await fetchCoverageData();
        }
      } else {
        console.warn(`[Brain.js] Setting coverage analysis failed (${response.status})`);
      }
    } catch (error) {
      console.error('[Brain.js] Error analyzing setting coverage:', error);
      // Don't throw - this is a non-critical operation
    }
  };

  // Fetch multilingual settings helper
  const fetchMultilingualSettings = async () => {
    try {
      const { getUserAccountId } = await import('../utils/accountUtils');
      const { accountId, error: accountIdError } = await getUserAccountId();
      if (accountIdError) throw new Error(accountIdError);

      const { data, error } = await supabase
        .from('vapi_call_settings')
        .select('multilingual')
        .eq('account_id', accountId)
        .maybeSingle();

      if (error) {
        if (error.code === 'PGRST116') {
          // No settings found - use defaults
          setGlobalMultilingualSettings({ multilingual: false });
        } else {
          throw error;
        }
      } else {
        setGlobalMultilingualSettings({
          multilingual: data?.multilingual || false
        });
      }
    } catch (error) {
      console.error('Error fetching multilingual settings:', error);
      setGlobalMultilingualSettings({ multilingual: false });
    } finally {
      // Mark multilingual settings as loaded
      completeLoadingOperation('multilingualSettings');
    }
  };

  // Real-time subscriptions - auto-refresh when mixed messages API completes
  useEffect(() => {
    if (!supabase) return;

    let subscription = null;

    const setupLiveUpdates = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Monitor brain_conflicts table for changes (mixed messages API updates this)
      subscription = supabase
        .channel('brain-conflicts-updates')
        .on('postgres_changes', {
          event: '*',
          schema: 'public', 
          table: 'brain_conflicts'
        }, () => {
          console.log('Live update: brain_conflicts changed');
          // Debounced refresh for smooth UX
          setTimeout(() => loadExistingConflicts(), 1000);
        })
        .subscribe();
    };

    setupLiveUpdates();

    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, [supabase]);

  // Update document title
  useEffect(() => {
    document.title = 'Brain | Savant AI';
  }, []);

  // Initialize header visibility and mobile functions
  useEffect(() => {
    setIsHeaderVisible(true);
  }, []);

  // Mobile header height calculation
  const calculateHeaderHeight = () => {
    let baseHeight = isMobile ? 75 : 70;
    const description = "Manage your AI's knowledge base with intelligent responses and automated actions";
    if (description) {
      baseHeight += isMobile ? 40 : 30;
    }
    if (isMobile) {
      baseHeight += 60;
    }
    const buffer = 5;
    return `${baseHeight + buffer}px`;
  };

  // Conflict navigation functions
  const nextConflict = () => {
    setCurrentConflictIndex((prev) => 
      prev < conflicts.length - 1 ? prev + 1 : 0
    );
  };

  const prevConflict = () => {
    setCurrentConflictIndex((prev) => 
      prev > 0 ? prev - 1 : conflicts.length - 1
    );
  };

  // Auto-select first setting when categories are loaded
  useEffect(() => {
    if (!selectedItem && categories.length > 0) {
      // Find the first category with settings
      for (const category of categories) {
        if (category.settings && category.settings.length > 0) {
          const firstSetting = category.settings[0];
          setSelectedItem({ 
            ...firstSetting, 
            type: 'setting', 
            category_name: category.name 
          });
          break;
        }
      }
    }
  }, [categories, selectedItem]);

  // Add voice command event listeners
  useEffect(() => {
    const handleOpenBrainModal = () => {
      setIsActionModalOpen(true);
    };

    const handleSelectBrainAction = (event) => {
      const { actionType } = event.detail;

      // Close the action modal first
      setIsActionModalOpen(false);

      // Use the same logic as handleActionSelect
      setTimeout(() => {
        handleActionSelect(actionType);
      }, 100); // Small delay to ensure modal closes first
    };

    // Add event listeners
    window.addEventListener('openBrainModal', handleOpenBrainModal);
    window.addEventListener('selectBrainAction', handleSelectBrainAction);

    // Cleanup
    return () => {
      window.removeEventListener('openBrainModal', handleOpenBrainModal);
      window.removeEventListener('selectBrainAction', handleSelectBrainAction);
    };
  }, []); // No dependencies needed

  // Fetch categories and their settings
  const fetchCategories = async () => {
    try {      
      // Don't set isLoading here - let the loading manager handle it
      setError(null);

      const { getUserAccountId } = await import('../utils/accountUtils');
      const { accountId, error: accountIdError } = await getUserAccountId();
      if (accountIdError || !accountId) throw new Error(accountIdError || 'Account ID not found');

      // Fetch locations for the account - we'll need this for location variants
      const { data: locationsData, error: locationsError } = await supabase
        .from('vapi_location_settings')
        .select('id, name, google_address')
        .eq('account_id', accountId)
        .order('name');

      if (locationsError) throw locationsError;
      setLocations(locationsData || []);

      // Fetch categories
      const { data: categoriesData, error: categoriesError } = await supabase
        .from('brain_categories')
        .select('*')
        .eq('account_id', accountId)
        .order('name');

      if (categoriesError) throw categoriesError;

      // Fetch settings for each category AND pre-load all detailed data including location variants
      const categoriesWithSettings = await Promise.all(
        categoriesData.map(async (category) => {
          const { data: settingsData, error: settingsError } = await supabase
            .from('brain_settings')
            .select('*')
            .eq('category_id', category.id);

          if (settingsError) {
            return { ...category, settings: [] };
          }

          // Pre-load ALL detailed data for each setting including location variants
          const fullyLoadedSettings = await Promise.all(
            (settingsData || []).map(async (setting) => {
              // Load location variants for this setting
              const locationVariants = await loadLocationVariants(setting.id, setting);

              if (setting.type === 'simple_action') {
                // Pre-load full simple action data
                const actionData = await loadSimpleAction(setting);
                return { 
                  ...setting, 
                  original_type: setting.type, // Add original_type for BrainMainContent
                  fullData: actionData,
                  action_type: actionData.type, // Extract action_type for sidebar display
                  locationVariants // Add location variants
                };
              } else if (setting.type === 'simple_answer') {
                // Pre-load full simple answer data
                const answerData = await loadSimpleAnswer(setting);
                return { 
                  ...setting, 
                  original_type: setting.type, // Add original_type for BrainMainContent
                  fullData: answerData,
                  answer_content: answerData.answer || null, // For conflict detection
                  answer_title: answerData.question || null, // For conflict detection
                  locationVariants // Add location variants
                };
              } else if (setting.type === 'complex_action') {
                // Pre-load complex action ID for immediate access
                try {
                  const { data: caRow } = await supabase
                    .from('brain_complex_actions')
                    .select('id')
                    .eq('settings_id', setting.id)
                    .limit(1)
                    .maybeSingle();
                  return { 
                    ...setting, 
                    original_type: setting.type, // Add original_type for BrainMainContent
                    fullData: { complexActionId: caRow?.id },
                    locationVariants // Add location variants
                  };
                } catch (err) {
                  return { 
                    ...setting, 
                    original_type: setting.type, // Add original_type for BrainMainContent
                    fullData: { complexActionId: null },
                    locationVariants // Add location variants
                  };
                }
              } else if (setting.type === 'complex_answer') {
                const locationVariants = await loadLocationVariants(setting.id, setting);
                const firstVariantData = locationVariants.length > 0 && locationVariants[0].fullData ? locationVariants[0].fullData : {};

                const formDataByGrouping = locationVariants.reduce((acc, variant) => {
                    if (variant.fullData) {
                        acc[variant.id] = variant.fullData;
                    }
                    return acc;
                }, {});

                const fullData = {
                  ...setting,
                  ...firstVariantData,
                  locationGroupings: locationVariants.map(v => ({id: v.id, name: v.name})),
                  locationAssignments: locationVariants.reduce((acc, v) => {
                      acc[v.id] = v.assignedLocationIds;
                      return acc;
                  }, {}),
                  formDataByGrouping,
                  mainGroupingId: locationVariants.length > 0 ? locationVariants[0].id : null,
                  showLocationVariants: locationVariants.length > 1
                };

                return {
                  ...setting,
                  original_type: setting.type,
                  fullData: fullData,
                  locationVariants
                };
              } else if (setting.type === 'workflow') {
                // For workflows, load the workflow data and steps
                try {
                  const { data: workflowRow } = await supabase
                    .from('brain_workflows')
                    .select('*')
                    .eq('settings_id', setting.id)
                    .limit(1)
                    .maybeSingle();

                  // Load workflow steps if workflow exists
                  let workflowSteps = [];
                  if (workflowRow) {
                    const { data: stepsData } = await supabase
                      .from('brain_workflow_steps')
                      .select('*')
                      .eq('workflow_id', workflowRow.id)
                      .order('step_order');

                    if (stepsData && stepsData.length > 0) {
                      // Load references and conditions for each step
                      const stepIds = stepsData.map(step => step.id);

                      // Load step references with brain item data
                      const { data: referencesData } = await supabase
                        .from('brain_workflow_step_references')
                        .select('*')
                        .in('workflow_step_id', stepIds)
                        .order('execution_order');

                      // Enhance references with action_type data for simple_action items
                      let enhancedReferences = referencesData || [];
                      if (enhancedReferences.length > 0) {
                        const simpleActionRefs = enhancedReferences.filter(ref => ref.reference_type === 'simple_action');
                        if (simpleActionRefs.length > 0) {
                          const simpleActionIds = simpleActionRefs.map(ref => ref.reference_id);
                          const { data: actionTypesData } = await supabase
                            .from('brain_simple_actions')
                            .select('settings_id, action_type')
                            .in('settings_id', simpleActionIds);

                          // Map action types back to references
                          const actionTypeMap = {};
                          (actionTypesData || []).forEach(action => {
                            actionTypeMap[action.settings_id] = action.action_type;
                          });

                          enhancedReferences = enhancedReferences.map(ref => ({
                            ...ref,
                            action_type: ref.reference_type === 'simple_action' ? actionTypeMap[ref.reference_id] : undefined,
                            subtype: ref.reference_type === 'simple_action' ? actionTypeMap[ref.reference_id] : undefined
                          }));
                        }
                      }

                      // Load step conditions
                      const { data: conditionsData } = await supabase
                        .from('brain_workflow_conditions')
                        .select('*')
                        .in('workflow_step_id', stepIds)
                        .order('condition_order');

                      // Map references and conditions to steps
                      workflowSteps = stepsData.map(step => ({
                        id: step.id,
                        name: step.step_name,
                        type: step.step_type,
                        order: step.step_order,
                        parentId: step.parent_step_id,
                        config: step.step_config || {},
                        position: step.node_position || null, // Load saved position
                        references: enhancedReferences.filter(ref => ref.workflow_step_id === step.id),
                        conditions: (conditionsData || []).filter(cond => cond.workflow_step_id === step.id)
                      }));
                    } else {
                      workflowSteps = [];
                    }
                  }

                  return { 
                    ...setting, 
                    original_type: setting.type, // Add original_type for BrainMainContent
                    fullData: {
                      ...(workflowRow || setting),
                      steps: workflowSteps
                    },
                    workflow_config: {
                      ...(workflowRow || {}),
                      steps: workflowSteps
                    },
                    locationVariants // Add location variants
                  };
                } catch (err) {
                  console.error('Error loading workflow data:', err);
                  return { 
                    ...setting, 
                    original_type: setting.type, // Add original_type for BrainMainContent
                    fullData: setting,
                    locationVariants // Add location variants
                  };
                }
              }
              return { 
                ...setting, 
                original_type: setting.type, // Add original_type for BrainMainContent
                locationVariants // Add location variants for any other types
              }; // Other types as-is
            })
          );

          return { ...category, settings: fullyLoadedSettings };
        })
      );

      setCategories(categoriesWithSettings);

    } catch (err) {
      console.error('Error fetching categories:', err);
      setError('Failed to load brain data. Please try again.');
    } finally {
      // Use the loading manager instead of directly setting isLoading
      completeLoadingOperation('categories');
    }
  };

  // Fetch Post Call Actions
  const fetchPostCallActions = async () => {
    try {
      const { getUserAccountId } = await import('../utils/accountUtils');
      const { accountId, error: accountIdError } = await getUserAccountId();
      if (accountIdError || !accountId) {
        setPostCallActions([]);
        return;
      }

      const { data: actions, error } = await supabase
        .from('brain_post_call_settings')
        .select(`
          *,
          brain_post_call_conditions (*)
        `)
        .eq('account_id', accountId)
        .order('priority', { ascending: true });

      if (error) throw error;

      // Map to frontend format
      const mappedActions = actions?.map(action => ({
        id: action.id,
        name: action.name,
        description: action.description,
        enabled: action.enabled,
        triggerType: action.trigger_type,
        actionType: action.action_type,
        action_id: action.action_id, // Add this field for loading existing actions
        priority: action.priority,
        conditions: action.brain_post_call_conditions || []
      })) || [];

      setPostCallActions(mappedActions);
    } catch (err) {
      console.error('Error fetching post call actions:', err);
      setPostCallActions([]);
    } finally {
      // Use the loading manager to mark post call actions as complete
      completeLoadingOperation('postCallActions');
    }
  };

  // Fetch Clarification Questions
  const fetchClarificationQuestions = async () => {
    try {
      const { getUserAccountId } = await import('../utils/accountUtils');
      const { accountId, error: accountIdError } = await getUserAccountId();
      if (accountIdError || !accountId) return;

      const { data, error } = await supabase
        .from('brain_conversation_topics')
        .select('*')
        .eq('account_id', accountId)
        .order('topic_name');

      if (error) throw error;
      setClarificationQuestions(data || []);
    } catch (err) {
      console.error('Error fetching clarification questions:', err);
      // Don't show a toast for this, as it's a non-critical background load
    } finally {
      completeLoadingOperation('clarificationQuestions');
    }
  };

  // Handler for adding a new Post Call Action
  const handleAddPostCallAction = () => {
    setEditingPostCallAction(null); // Clear for new action
    setIsPostCallActionModalOpen(true);
  };

  // Handler for selecting a Post Call Action
  const handlePostCallActionSelect = (action) => {
    setSelectedItem(action);
    // Just select the item, don't open modal automatically
    // Modal will open when user clicks edit button in BrainMainContent
  };

  // Handler for toggling Post Call Action expansion (if needed for future)
  const handleTogglePostCallAction = (actionId) => {
    const newExpanded = new Set(expandedPostCallActions);
    if (newExpanded.has(actionId)) {
      newExpanded.delete(actionId);
    } else {
      newExpanded.add(actionId);
    }
    setExpandedPostCallActions(newExpanded);
  };

  // Helper function to load location variants for a setting
  const loadLocationVariants = async (settingsId, setting) => {
    try {
      // Fetch groupings for this setting
      const { data: groupingsData, error: groupingsError } = await supabase
        .from('brain_groupings')
        .select('*')
        .eq('settings_id', settingsId)
        .order('name');

      if (groupingsError) throw groupingsError;

      if (!groupingsData || groupingsData.length === 0) {
        return [];
      }

      // Fetch location assignments for these groupings
      const groupingIds = groupingsData.map(g => g.id);
      const { data: locationsData, error: locationsError } = await supabase
        .from('brain_grouping_locations')
        .select('grouping_id, location_id')
        .in('grouping_id', groupingIds);

      if (locationsError) throw locationsError;

      // Map location assignments to groupings
      const locationAssignments = {};
      (locationsData || []).forEach(row => {
        if (!locationAssignments[row.grouping_id]) {
          locationAssignments[row.grouping_id] = [];
        }
        locationAssignments[row.grouping_id].push(row.location_id);
      });

      // Pre-load actual content data for each variant based on setting type
      const variants = await Promise.all(groupingsData.map(async (grouping) => {
        let variantData = {
          id: grouping.id,
          name: grouping.name,
          settingsId: grouping.settings_id,
          assignedLocationIds: locationAssignments[grouping.id] || [],
          created_at: grouping.created_at
        };

        // Load type-specific content for this variant
        if (setting.type === 'simple_action') {
          // Load simple action data for this specific grouping
          const { data: saRows } = await supabase
            .from('brain_simple_actions')
            .select('*')
            .eq('settings_id', settingsId)
            .eq('grouping_id', grouping.id);

          if (saRows && saRows.length > 0) {
            const saRow = saRows[0];
            const actionType = saRow.action_type || 'sms';

            // Load type-specific data
            let actionData = {
              id: saRow.id,
              settings_id: settingsId,
              name: setting.name,
              description: setting.description || '',
              triggers: setting.triggers || [],
              type: actionType,
              category_id: setting.category_id,
              grouping_id: grouping.id
            };

            // Load variant-specific content based on action type
            if (actionType === 'sms') {
              const { data: smsRow } = await supabase
                .from('brain_simple_actions_sms')
                .select('*')
                .eq('simple_action_id', saRow.id)
                .maybeSingle();
              if (smsRow) {
                actionData.sms_message = smsRow.message || '';
                actionData.sms_use_contact_phone = smsRow.use_contact_phone || false;
              }
            } else if (actionType === 'email') {
              const { data: emailRow } = await supabase
                .from('brain_simple_actions_email')
                .select('*')
                .eq('simple_action_id', saRow.id)
                .maybeSingle();
              if (emailRow) {
                actionData.email_subject = emailRow.subject || '';
                actionData.email_message = emailRow.message || '';
                actionData.email_use_contact_email = emailRow.use_contact_email || false;
              }
            } else if (actionType === 'transfer') {
              const { data: transferRow } = await supabase
                .from('brain_simple_actions_transfer_call')
                .select('*')
                .eq('simple_action_id', saRow.id)
                .maybeSingle();
              if (transferRow) {
                actionData.transfer_number = transferRow.phone_number || '';
                actionData.transfer_name = transferRow.name || '';
              }
            } else if (actionType === 'api') {
              const { data: apiRow } = await supabase
                .from('brain_simple_actions_api')
                .select('*')
                .eq('simple_action_id', saRow.id)
                .maybeSingle();
              if (apiRow) {
                actionData.api_endpoint = apiRow.url || '';
                actionData.api_method = apiRow.method || 'POST';
                actionData.api_body = apiRow.body ? JSON.stringify(apiRow.body) : '';
                actionData.api_headers = apiRow.headers || {};
              }
            } else if (actionType === 'dtmf') {
              const { data: dtmfRow } = await supabase
                .from('brain_simple_actions_dtmf')
                .select('*')
                .eq('simple_action_id', saRow.id)
                .maybeSingle();
              if (dtmfRow) {
                actionData.dtmf_number_to_press = dtmfRow.number_to_press || '';
              }
            } else if (actionType === 'note') {
                const { data: fieldsData, error: fieldsError } = await supabase
                    .from('brain_simple_actions_fields')
                    .select('*')
                    .eq('simple_action_id', saRow.id)
                    .order('order');

                if (fieldsError) throw fieldsError;

                if (fieldsData && fieldsData.length > 0) {
                    const fieldIds = fieldsData.map(f => f.id);
                    const { data: enumsData, error: enumsError } = await supabase
                        .from('brain_simple_actions_fields_enum')
                        .select('*')
                        .in('simple_action_field_id', fieldIds);

                    if (enumsError) throw enumsError;

                    actionData.note_fields_config = fieldsData.map(field => {
                        const enums = (enumsData || [])
                            .filter(e => e.simple_action_field_id === field.id)
                            .map(e => e.enum);

                        let parentTempId = null;
                        if (field.parent_field_id) {
                            const parentFieldInLoad = fieldsData.find(f => f.id === field.parent_field_id);
                            if (parentFieldInLoad) parentTempId = parentFieldInLoad.id;
                        }

                        return {
                            id: field.id,
                            temp_id: field.id,
                            question: field.question,
                            type: field.type,
                            order: field.order,
                            options: enums,
                            parent_field_id: field.parent_field_id,
                            parent_field_temp_id: parentTempId,
                            criteria: field.criteria
                        };
                    });
                } else {
                    actionData.note_fields_config = [];
                }
            }
            // Add other action types as needed

            variantData.fullData = actionData;
          }
        } else if (setting.type === 'simple_answer') {
          // Load simple answer data for this specific grouping
          const { data: answerRows } = await supabase
            .from('brain_simple_answers')
            .select('*')
            .eq('settings_id', settingsId)
            .eq('grouping_id', grouping.id);

          if (answerRows && answerRows.length > 0) {
            const answerRow = answerRows[0];
            variantData.fullData = {
              id: answerRow.id,
              settings_id: settingsId,
              name: setting.name,
              question: answerRow.title || setting.name || '',
              answer: answerRow.content || '',
              category_id: setting.category_id,
              grouping_id: grouping.id
            };
          }
        } else if (setting.type === 'complex_answer') {
          // Load complex answer data for this specific grouping
          const { data: complexRows } = await supabase
            .from('brain_complex_answers')
            .select('*')
            .eq('settings_id', settingsId)
            .eq('grouping_id', grouping.id);

          if (complexRows && complexRows.length > 0) {
            const complexRow = complexRows[0];
            let apiData = null;
            let questionsData = [];

            if (complexRow.answer_type === 'api') {
                const { data: apiRows } = await supabase
                    .from('brain_complex_answers_api')
                    .select('*')
                    .eq('complex_answer_id', complexRow.id);
                if (apiRows && apiRows.length > 0) apiData = apiRows[0];
            }

            const { data: questionRows } = await supabase
                .from('brain_complex_answers_questions')
                .select('*')
                .eq('complex_answer_id', complexRow.id)
                .order('order');
            if (questionRows) questionsData = questionRows;

            variantData.fullData = {
              id: complexRow.id,
              settings_id: settingsId,
              name: setting.name,
              description: setting.description,
              triggers: setting.triggers,
              content: complexRow.content || '',
              type: complexRow.answer_type || 'knowledge',
              grouping_id: grouping.id,
              api_config: apiData,
              questions: questionsData,
            };
          }
        }

        return variantData;
      }));

      return variants;

    } catch (error) {
      console.error('Error loading location variants:', error);
      return [];
    }
  };

  // Load existing conflicts from database without triggering new analysis
  const loadExistingConflicts = async () => {
    try {
      setIsLoadingConflicts(true);
      const existingConflicts = await getExistingConflicts(supabase);
      setConflicts(existingConflicts);
    } catch (error) {
      console.error('Error loading existing conflicts:', error);
    } finally {
      setIsLoadingConflicts(false);
    }
  };

  // NEW: Wrapper function for conflict detection with processing animation
  const runConflictDetectionWithAnimation = async () => {
    try {
      setIsProcessingConflicts(true);
      await runAutomaticConflictDetection(supabase, updateConflicts);
    } catch (error) {
      console.error('Error running conflict detection:', error);
    } finally {
      // Add a small delay to ensure the animation is visible
      setTimeout(() => {
        setIsProcessingConflicts(false);
      }, 500);
    }
  };

  const handleAddClick = (categoryId = null) => {
    setPreSelectedCategoryId(categoryId);
    setIsActionModalOpen(true);
  };

  const handleActionSelect = (actionType) => {
    // Close the action modal first
    setIsActionModalOpen(false);

    // For inline editing types, create a new setting object and select it
    if (['simple_answer', 'complex_answer', 'simple_action', 'workflow'].includes(actionType)) {
      const newSetting = {
        type: 'setting',
        original_type: actionType,
        id: `new-${actionType}-${Date.now()}`, // Temporary ID for new items
        name: '',
        description: '',
        triggers: [],
        category_id: preSelectedCategoryId || (categories.length > 0 ? categories[0].id : null),
        isNew: true,
        isPreLoaded: false
      };

      setSelectedItem(newSetting);
      return;
    }

    // Handle other action types as before
    switch (actionType) {
      case 'complex_action':
        setCurrentSetting(null);
        setEditingPathwayId(null);
        setTimeout(() => {
          setIsOrderPathwayModalOpen(true);
        }, 0);
        break;
      case 'create_category':
        setPreSelectedCategoryId(null);
        setIsNewCategoryModalOpen(true);
        break;
      case 'smart_upload':
        setIsChatOpen(true);
        break;
      default:
        toast.error(`Unknown action type: ${actionType}`);
    }
  };

  // Handle editing a category or setting
  const handleEditCategory = (item) => {
    // Check if this is a Post Call Action
    if (item.type === 'postCallAction') {
      // Only open modal for NEW Post Call Actions
      if (item.isNew) {
        setEditingPostCallAction(item);
        setIsPostCallActionModalOpen(true);
      }
      // For existing Post Call Actions, do nothing - they show inline content
    } else if (item.settings) {
      // This is a category
      setEditingCategory(item); // Set the category to edit
      setIsNewCategoryModalOpen(true); // Open the NewCategoryModal
    } else {
      // This is a setting - dispatch based on type
      handleEditSetting(item);
    }
  };

  // Handle deleting a category
  const handleDeleteCategory = async (category) => {
    try {
      // Check what type of item we're deleting
      if (category.type === 'variant') {
        // This is a location variant - delete from brain_groupings
        console.log('Deleting variant:', category);
        console.log('Variant ID:', category.id);

        const { data, error } = await supabase
          .from('brain_groupings')
          .delete()
          .eq('id', category.id)
          .select(); // Add select to see what was deleted

        console.log('Delete result:', { data, error });

        if (error) {
          console.error('Delete error:', error);
          throw error;
        }

        if (!data || data.length === 0) {
          console.warn('No rows were deleted - variant might not exist or RLS policy blocking');
          toast.error('Failed to delete version - it may not exist');
          return;
        }

        // Clear selected item if it's the deleted variant
        if (selectedItem && selectedItem.id === category.id) {
          setSelectedItem(null);
        }

        // Update local state to remove the variant from the UI immediately
        setCategories(prevCategories => 
          prevCategories.map(cat => ({
            ...cat,
            settings: cat.settings?.map(setting => ({
              ...setting,
              locationVariants: setting.locationVariants?.filter(variant => variant.id !== category.id) || []
            })) || []
          }))
        );

        toast.success(`"${category.name}" version deleted successfully`);
      } else if (category.settings) {
        // This is a category
        // Cleanup calls_analysis_onboarding table for all settings in this category
        try {
          await cleanupCallsAnalysisOnboarding(accountId, businessType, null, category.id);
        } catch (cleanupError) {
          console.error('Error cleaning up calls analysis onboarding for category:', cleanupError);
          // Continue with deletion even if cleanup fails
        }

        const { error } = await supabase
          .from('brain_categories')
          .delete()
          .eq('id', category.id);

        if (error) throw error;

        // Update local state
        setCategories(categories.filter(c => c.id !== category.id));

        // Clear selected item if it's the deleted category
        if (selectedItem && selectedItem.id === category.id) {
          setSelectedItem(null);
        }

        toast.success(`"${category.name}" deleted successfully`);
      } else {
        // This is an individual setting
        // Cleanup calls_analysis_onboarding table for this setting
        try {
          await cleanupCallsAnalysisOnboarding(accountId, businessType, category.id, null);
        } catch (cleanupError) {
          console.error('Error cleaning up calls analysis onboarding for setting:', cleanupError);
          // Continue with deletion even if cleanup fails
        }

        const { error } = await supabase
          .from('brain_settings')
          .delete()
          .eq('id', category.id);

        if (error) throw error;

        // Clear selected item if it's the deleted setting
        if (selectedItem && selectedItem.id === category.id) {
          setSelectedItem(null);
        }

        // Remove setting from local state instead of full page refresh
        removeSettingFromCategories(category.id);
        toast.success(`"${category.name}" deleted successfully`);
      }

      // After successful delete
      refreshBrainSettings(); // Refresh AI context
    } catch (err) {
      console.error('Error deleting item:', err);
      toast.error('Failed to delete item');
    }
  };

  const handleDuplicateSetting = async (settingToDuplicate) => {
    toast.info(`Duplicating "${settingToDuplicate.name}"...`);

    const supabaseTransaction = async (operations) => {
      // This is a simplified transaction-like wrapper.
      // If any operation fails, subsequent ones won't run,
      // but it won't roll back completed ones.
      // For a true transaction, a db function (RPC) would be better.
      try {
        for (const op of operations) {
          await op();
        }
      } catch (error) {
        console.error("Duplication failed:", error);
        toast.error(`Failed to duplicate: ${error.message}`);
        throw error;
      }
    };

    try {
      const { getUserAccountId } = await import('../utils/accountUtils');
      await getUserAccountId();

      // 1. Create the new setting
      const { data: newSetting, error: settingError } = await supabase
        .from('brain_settings')
        .insert({
          category_id: settingToDuplicate.category_id,
          name: `${settingToDuplicate.name} (Copy)`,
          description: settingToDuplicate.description,
          triggers: settingToDuplicate.triggers,
          type: settingToDuplicate.original_type || settingToDuplicate.type,
          status: settingToDuplicate.status,
        })
        .select()
        .single();

      if (settingError) throw settingError;

      const duplicateGroupingsAndContent = async (originalSetting, newSetting) => {
        const fullData = originalSetting.fullData;
        if (!fullData || !fullData.locationGroupings || !fullData.formDataByGrouping) {
          console.warn("Missing fullData for duplication, skipping variants", originalSetting);
          return;
        }

        const groupings = [...fullData.locationGroupings];
        // If there are no explicit groupings, there's still a main/default one
        if (groupings.length === 0 && fullData.mainGroupingId) {
            groupings.push({ id: fullData.mainGroupingId, name: 'Default' });
        }

        for (const grouping of groupings) {
            const { data: newGrouping, error: groupingError } = await supabase
              .from('brain_groupings')
              .insert({ settings_id: newSetting.id, name: grouping.name })
              .select().single();
            if (groupingError) throw groupingError;

            // Duplicate location assignments for the grouping
            const { data: locations } = await supabase.from('brain_grouping_locations').select('location_id').eq('grouping_id', grouping.id);
            if (locations && locations.length > 0) {
                await supabase.from('brain_grouping_locations').insert(
                    locations.map(l => ({ grouping_id: newGrouping.id, location_id: l.location_id }))
                );
            }

            // Duplicate the actual content based on type
            const variantData = fullData.formDataByGrouping[grouping.id];
            if (!variantData) continue;

            switch (originalSetting.original_type) {
              case 'simple_answer':
                await supabase.from('brain_simple_answers').insert({
                    settings_id: newSetting.id,
                    grouping_id: newGrouping.id,
                    title: variantData.question,
                    content: variantData.answer
                });
                break;
              case 'simple_action':
                const { data: newSimpleAction, error: saError } = await supabase.from('brain_simple_actions').insert({
                    settings_id: newSetting.id,
                    grouping_id: newGrouping.id,
                    action_type: fullData.type,
                }).select().single();
                if (saError) throw saError;

                switch(fullData.type) {
                  case 'sms':
                    const { data: newSmsAction, error: smsError } = await supabase.from('brain_simple_actions_sms').insert({
                        simple_action_id: newSimpleAction.id,
                        message: variantData.sms_message,
                        use_contact_phone: variantData.sms_use_contact_phone
                    }).select().single();

                    if (smsError) throw smsError;

                    if (variantData.use_contact_phone && variantData.sms_numbers && Array.isArray(variantData.sms_numbers) && variantData.sms_numbers.length > 0) {
                        const numbersToInsert = variantData.sms_numbers.map(number => ({
                            simple_action_sms_id: newSmsAction.id,
                            phone_number: number
                        }));
                        const { error: numbersError } = await supabase.from('brain_simple_actions_sms_numbers').insert(numbersToInsert);
                        if (numbersError) throw numbersError;
                    }
                    break;
                  case 'email':
                     const { data: newEmailAction, error: emailError } = await supabase.from('brain_simple_actions_email').insert({
                        simple_action_id: newSimpleAction.id,
                        subject: variantData.email_subject,
                        message: variantData.email_message,
                        use_contact_email: variantData.email_use_contact_email
                    }).select().single();

                    if (emailError) throw emailError;

                    if (variantData.use_contact_email && variantData.email_addresses && variantData.email_addresses.length > 0) {
                        const addressesToInsert = variantData.email_addresses.map(address => ({
                            simple_action_email_id: newEmailAction.id,
                            email_address: address
                        }));
                        await supabase.from('brain_simple_actions_email_addresses').insert(addressesToInsert);
                    }
                    break;
                  case 'transfer':
                    await supabase.from('brain_simple_actions_transfer_call').insert({
                        simple_action_id: newSimpleAction.id,
                        phone_number: variantData.transfer_number,
                        name: variantData.transfer_name,
                        start_time: variantData.transfer_start_time,
                        end_time: variantData.transfer_end_time,
                        days_of_week: variantData.transfer_days,
                        play_message: variantData.play_message,
                        transfer_message: variantData.transfer_message
                    });
                    break;
                  case 'api':
                    await supabase.from('brain_simple_actions_api').insert({
                        simple_action_id: newSimpleAction.id,
                        url: variantData.api_endpoint,
                        method: variantData.api_method,
                        headers: variantData.api_headers,
                        body: variantData.api_body ? JSON.parse(variantData.api_body) : null,
                        api_query_params: variantData.api_query_params || {},
                        response_template: typeof variantData.response_template === 'string' && variantData.response_template.trim() !== ''
                               ? JSON.parse(variantData.response_template)
                               : (variantData.response_template || {})
                    });
                    break;
                  case 'dtmf':
                    await supabase.from('brain_simple_actions_dtmf').insert({
                        simple_action_id: newSimpleAction.id,
                        number_to_press: variantData.dtmf_number_to_press
                    });
                    break;
                  case 'note':
                    const fieldsToDuplicate = variantData.note_fields_config;
                    if (fieldsToDuplicate && fieldsToDuplicate.length > 0) {
                        const oldToNewFieldIdMap = {};
                        const newFieldsData = [];

                        for(const field of fieldsToDuplicate) {
                            const { data: newField, error } = await supabase.from('brain_simple_actions_fields').insert({
                                simple_action_id: newSimpleAction.id,
                                question: field.question,
                                type: field.type,
                                order: field.order,
                                criteria: field.criteria,
                            }).select().single();
                            if (error) throw error;
                            oldToNewFieldIdMap[field.id] = newField.id;
                            newFieldsData.push({ ...field, newId: newField.id });

                            if (field.type === 'enum' && field.options && field.options.length > 0) {
                                const enumsToInsert = field.options.map(opt => ({
                                    simple_action_field_id: newField.id,
                                    enum: opt
                                }));
                                await supabase.from('brain_simple_actions_fields_enum').insert(enumsToInsert);
                            }
                        }

                        for (const field of newFieldsData) {
                            if (field.parent_field_id) {
                                const newParentId = oldToNewFieldIdMap[field.parent_field_id];
                                if (newParentId) {
                                    await supabase.from('brain_simple_actions_fields')
                                        .update({ parent_field_id: newParentId })
                                        .eq('id', field.newId);
                                }
                            }
                        }
                    }
                    break;
                }

                if (variantData.questions && variantData.questions.length > 0) {
                    const questionsToInsert = variantData.questions.map(q => ({
                        simple_action_id: newSimpleAction.id,
                        order: q.order,
                        question: q.question,
                        type: q.type
                    }));
                    await supabase.from('brain_simple_actions_questions').insert(questionsToInsert);
                }

                break;
              case 'complex_answer':
                const complexAnswerVariantData = fullData.formDataByGrouping[grouping.id];
                if (!complexAnswerVariantData) continue;

                const answerType = complexAnswerVariantData.type || 'knowledge';
                const { data: newComplexAnswer, error: caError } = await supabase.from('brain_complex_answers').insert({
                    settings_id: newSetting.id,
                    grouping_id: newGrouping.id,
                    content: answerType === 'api' ? null : (complexAnswerVariantData.content || ''),
                    answer_type: answerType
                }).select().single();
                if (caError) throw caError;

                // Duplicate questions
                if (complexAnswerVariantData.questions && complexAnswerVariantData.questions.length > 0) {
                    const questionsToInsert = complexAnswerVariantData.questions.map(q => ({
                        complex_answer_id: newComplexAnswer.id,
                        order: q.order,
                        question: q.question,
                        type: q.type
                    }));
                    await supabase.from('brain_complex_answers_questions').insert(questionsToInsert);
                }

                // Duplicate API config
                if (complexAnswerVariantData.type === 'api' && complexAnswerVariantData.api_config) {
                    const { id, created_at, updated_at, complex_answer_id, ...apiConfigToCopy } = complexAnswerVariantData.api_config;
                    await supabase.from('brain_complex_answers_api').insert({
                        ...apiConfigToCopy,
                        complex_answer_id: newComplexAnswer.id
                    });
                }
                break;
            }
        }
      };

      if (['simple_answer', 'simple_action', 'complex_answer'].includes(settingToDuplicate.original_type)) {
        await duplicateGroupingsAndContent(settingToDuplicate, newSetting);
      } else {
        toast.error(`Duplication for "${settingToDuplicate.original_type.replace(/_/g, ' ')}" is not supported yet.`);
        // Clean up the created setting if duplication is not supported
        await supabase.from('brain_settings').delete().eq('id', newSetting.id);
        return;
      }

      toast.success(`"${settingToDuplicate.name}" duplicated successfully.`);
      await fetchCategories(); // Refresh UI
      runConflictDetectionWithAnimation();

    } catch (error) {
      console.error('Error duplicating setting:', error);
      toast.error(`Failed to duplicate setting: ${error.message}`);
    }
  };

  // Handle editing a brain setting
  const handleEditSetting = async (setting) => {
    // Post Call Actions are handled separately, not through this function
    if (setting.type === 'postCallAction') {
      // Post Call Actions are handled in BrainMainContent inline, not here
      return;
    }

    // OPTIMIZATION: Use pre-loaded data for instant opening
    if (setting.type === 'simple_action') {
      if (setting.fullData) {
        // Mark as pre-loaded to skip all loading states in modal
        setCurrentSetting({ ...setting.fullData, isPreLoaded: true });
        setIsSimpleActionModalOpen(true);
      } else {
        toast.error('Simple action data not loaded');
      }
      return;
    }

    if (setting.type === 'simple_answer') {
      if (setting.fullData) {
        // Mark as pre-loaded to skip all loading states in modal
        setCurrentSetting({ ...setting.fullData, isPreLoaded: true });
        // For inline editing, just select the item
        setSelectedItem({ ...setting, type: 'setting' });
      } else {
        toast.error('Simple answer data not loaded');
      }
      return;
    }

    if (setting.type === 'complex_action') {
      if (setting.fullData && setting.fullData.complexActionId) {
        setEditingPathwayId(setting.fullData.complexActionId);
        setIsOrderPathwayModalOpen(true);
      } else {
        toast.error('Complex action details not found');
      }
      return;
    }

    if (setting.type === 'complex_answer') {
      // OPTIMIZATION: Pre-populate form data synchronously to eliminate render cycle delays
      const formData = {
        id: setting.id,
        category_id: setting.category_id || '',
        name: setting.name || '',
        description: setting.description || '',
        triggers: Array.isArray(setting.triggers) ? [...setting.triggers] : [],
        content: setting.content || '',
        type: 'text',
        showLocationVariants: false,
        // Include all other necessary fields for immediate rendering
        questions: [],
        all_locations: false,
        selected_locations: []
      };

      // Mark as pre-loaded to skip all loading states
      setCurrentSetting({ ...setting, formData, isPreLoaded: true });

      // For inline editing, just select the item
      setSelectedItem({ ...setting, type: 'setting' });
      return;
    }

    if (setting.type === 'workflow') {
      // For workflows, use pre-loaded data if available
      if (setting.fullData || setting.workflow_config) {
        setCurrentSetting({ ...setting, isPreLoaded: true });
        setIsWorkflowModalOpen(true);
      } else {
        // Basic workflow data
        setCurrentSetting({ ...setting, isPreLoaded: true });
        setIsWorkflowModalOpen(true);
      }
      return;
    }

    // Fallback for unknown types
    toast.error('Unknown setting type');
  };

  // Helper to load all data for a simple_action so the CallActionSettingsModal is fully populated
  const loadSimpleAction = async (setting) => {
    // 1. Get ALL simple_action rows for this setting so we can map each grouping -> payload
    const { data: saRows, error: saErr } = await supabase
      .from('brain_simple_actions')
      .select('*')
      .eq('settings_id', setting.id);
    if (saErr) throw saErr;

    // If we have zero rows we still want to allow editing – fallback to blank defaults
    const firstRow = saRows && saRows.length ? saRows[0] : null;
    const actionType = firstRow?.action_type || 'sms';

    // Base action object shared across groupings
    const action = {
      id: firstRow?.id || null, // can be used as fallback
      settings_id: setting.id,
      name: setting.name,
      description: setting.description || '',
      triggers: setting.triggers || [],
      type: actionType,
      category_id: setting.category_id,
      // UI defaults – will be overwritten after we load grouping-specific data
      is_active: true,
      all_locations: true,
      selected_locations: [],
    };

    // 2. Fetch location groupings for this setting so the side-panel can show them
    const { data: groupRows } = await supabase
      .from('brain_groupings')
      .select('*')
      .eq('settings_id', setting.id)
      .order('created_at');
    const locationGroupings = groupRows || [];

    // 3. Build assignment map (grouping_id -> [location_ids])
    let locationAssignments = {};
    if (locationGroupings.length) {
      const groupIds = locationGroupings.map((g) => g.id);
      const { data: locRows } = await supabase
        .from('brain_grouping_locations')
        .select('grouping_id, location_id')
        .in('grouping_id', groupIds);
      (locRows || []).forEach((row) => {
        if (!locationAssignments[row.grouping_id]) locationAssignments[row.grouping_id] = [];
        locationAssignments[row.grouping_id].push(row.location_id);
      });
    }

    // 4. Build mapping of grouping -> type-specific form fields
    const formDataByGrouping = {};

    // Helper per actionType so code stays lean
    const loadSMSVariant = async (saRow) => {
      const { data: smsRow } = await supabase
        .from('brain_simple_actions_sms')
        .select('*') // Select all fields
        .eq('simple_action_id', saRow.id)
        .maybeSingle();
      if (!smsRow) return {}; // Return empty if no SMS config found

      let smsNumbers = [];
      if (smsRow.use_contact_phone) {
        const { data: nums } = await supabase
          .from('brain_simple_actions_sms_numbers')
          .select('phone_number')
          .eq('simple_action_sms_id', smsRow.id);
        smsNumbers = (nums || []).map((n) => n.phone_number);
      }

      return {
        sms_message: smsRow.message || '',
        sms_use_contact_phone: smsRow.use_contact_phone || false,
        sms_numbers: smsNumbers
      };
    };

    const loadEmailVariant = async (saRow) => {
      // Fetch the main email settings row
      const { data: emailRow, error: emailRowError } = await supabase
        .from('brain_simple_actions_email')
        .select('*') // Select all fields
        .eq('simple_action_id', saRow.id)
        .maybeSingle();

      // Handle potential error fetching email row
      if (emailRowError) {
      }

      // If no email row found (maybeSingle returned null), return default structure
      if (!emailRow) {
          return {
              email_subject: '',
              email_message: '',
              email_use_contact_email: false,
              email_addresses: [],
          };
      }

      // Prepare default email addresses array
      let emailAddresses = [];

      // Fetch addresses only if use_contact_email is true and we have a valid emailRow.id
      if (emailRow.use_contact_email && emailRow.id) {
        const { data: addrs, error: addrsError } = await supabase
          .from('brain_simple_actions_email_addresses')
          .select('email_address')
          .eq('simple_action_email_id', emailRow.id); // Foreign key join

        // Handle potential error fetching addresses
        if (addrsError) {
        } else {
            // If fetch was successful (even if empty), map the results
            emailAddresses = (addrs || []).map((a) => a.email_address);
        }
      }

      // Construct the final variant data object
      const variantResult = {
        email_subject: emailRow.subject || '',
        email_message: emailRow.message || '',
        email_use_contact_email: emailRow.use_contact_email || false,
        email_addresses: emailAddresses, // Use the populated or default empty array
      };
      return variantResult;
    };

    const loadTransferVariant = async (saRow) => {
      const { data: trRow } = await supabase
        .from('brain_simple_actions_transfer_call')
        .select('*') // Select all fields
        .eq('simple_action_id', saRow.id)
        .maybeSingle();
      if (!trRow) return {}; // Return empty if no Transfer config found

      return {
        transfer_number: trRow.phone_number || '',
        transfer_name: trRow.name || '',
        transfer_start_time: trRow.start_time || '09:00', // Default if null
        transfer_end_time: trRow.end_time || '17:00',     // Default if null
        transfer_days: trRow.days_of_week || [0,1,2,3,4,5,6], // Default to all days if null
        play_message: trRow.play_message || false, // Fetch play_message, default false
        transfer_message: trRow.transfer_message || '' // Fetch transfer_message, default empty
      };
    };

    const loadAPIVariant = async (saRow) => {
      const { data: apiRow } = await supabase
        .from('brain_simple_actions_api')
        .select('*') // Select all fields
        .eq('simple_action_id', saRow.id)
        .maybeSingle();
      if (!apiRow) return {}; // Return empty if no API config found

      return {
        api_endpoint: apiRow.url || '', // Map url to api_endpoint
        api_method: apiRow.method || 'POST', // Map method to api_method
        api_headers: apiRow.headers || {}, // Map headers (JSONB) to object, default {}
        api_body: apiRow.body ? JSON.stringify(apiRow.body, null, 2) : '', // Map body (JSONB) to stringified JSON, default ''
        api_query_params: apiRow.api_query_params || {},
        response_template: apiRow.response_template || '{}'
      };
    };

    const loadDTMFVariant = async (saRow) => {
        const { data: dtmfRow } = await supabase
            .from('brain_simple_actions_dtmf')
            .select('*')
            .eq('simple_action_id', saRow.id)
            .maybeSingle();
        return { dtmf_number_to_press: dtmfRow?.number_to_press || '' };
    };

    const loadNoteVariant = async (saRow) => {
        const { data: fieldsData, error: fieldsError } = await supabase
            .from('brain_simple_actions_fields')
            .select('*')
            .eq('simple_action_id', saRow.id)
            .order('order');
        if (fieldsError) throw fieldsError;

        if (!fieldsData || fieldsData.length === 0) {
            return { note_fields_config: [] };
        }

        const fieldIds = fieldsData.map(f => f.id);
        const { data: enumsData, error: enumsError } = await supabase
            .from('brain_simple_actions_fields_enum')
            .select('*')
            .in('simple_action_field_id', fieldIds);
        if (enumsError) throw enumsError;

        const note_fields_config = fieldsData.map(field => {
            const enums = (enumsData || [])
                .filter(e => e.simple_action_field_id === field.id)
                .map(e => e.enum);
            return {
                id: field.id,
                temp_id: field.id,
                question: field.question,
                type: field.type,
                order: field.order,
                options: enums,
                parent_field_id: field.parent_field_id,
                criteria: field.criteria
            };
        });
        return { note_fields_config };
    };

    const loadQuestionsForAction = async (simpleActionId) => {
        const { data, error } = await supabase
            .from('brain_simple_actions_questions')
            .select('*')
            .eq('simple_action_id', simpleActionId);
        if(error) {
            console.error("Error loading simple action questions", error);
            return [];
        }
        return data || [];
    };

    // Iterate all simple_action rows & build variant map
    for (const saRow of saRows || []) {
      let variantData = {};
      if (actionType === 'sms') variantData = await loadSMSVariant(saRow);
      else if (actionType === 'email') variantData = await loadEmailVariant(saRow);
      else if (actionType === 'transfer') variantData = await loadTransferVariant(saRow);
      else if (actionType === 'api') variantData = await loadAPIVariant(saRow);
      else if (actionType === 'dtmf') variantData = await loadDTMFVariant(saRow);
      else if (actionType === 'note') variantData = await loadNoteVariant(saRow);

      variantData.questions = await loadQuestionsForAction(saRow.id);

      formDataByGrouping[saRow.grouping_id] = variantData;

      // For convenience, seed the main action object with the first row's data
      if (saRow.id === firstRow?.id) {
        Object.assign(action, variantData);
      }
    }

    // Attach grouping / variant info to action object
    action.locationGroupings = locationGroupings;
    action.locationAssignments = locationAssignments;
    action.showLocationVariants = locationGroupings.length > 1;
    action.formDataByGrouping = formDataByGrouping;
    action.mainGroupingId = firstRow?.grouping_id || null;

    return action;
  };

  // Helper to load all data for a simple_answer
  const loadSimpleAnswer = async (setting) => {
    // 1. Get ALL simple_answer rows for this setting
    const { data: answerRows, error: answerErr } = await supabase
      .from('brain_simple_answers')
      .select('*')
      .eq('settings_id', setting.id);
    if (answerErr) throw answerErr;

    const firstRow = answerRows && answerRows.length ? answerRows[0] : null;

    // Base answer object
    const answer = {
      id: firstRow?.id || null, // Use first answer row ID as a reference if needed
      settings_id: setting.id,
      name: setting.name, // Initial name from brain_settings
      question: firstRow?.title || setting.name || '', // Use title from first answer row or setting name
      answer: firstRow?.content || '', // Use content from first answer row
      category_id: setting.category_id,
    };

    // 2. Fetch location groupings
    const { data: groupRows } = await supabase
      .from('brain_groupings')
      .select('*')
      .eq('settings_id', setting.id)
      .order('created_at');
    const locationGroupings = groupRows || [];

    // 3. Build assignment map
    let locationAssignments = {};
    if (locationGroupings.length) {
      const groupIds = locationGroupings.map((g) => g.id);
      const { data: locRows } = await supabase
        .from('brain_grouping_locations')
        .select('grouping_id, location_id')
        .in('grouping_id', groupIds);
      (locRows || []).forEach((row) => {
        if (!locationAssignments[row.grouping_id]) locationAssignments[row.grouping_id] = [];
        locationAssignments[row.grouping_id].push(row.location_id);
      });
    }

    // 4. Build mapping of grouping -> form fields (question/answer)
    const formDataByGrouping = {};
    (answerRows || []).forEach(row => {
      formDataByGrouping[row.grouping_id] = {
        question: row.title || '',
        answer: row.content || ''
      };
      // Update the main answer object with the first grouping's data
      if (row.grouping_id === firstRow?.grouping_id) {
        answer.question = row.title || '';
        answer.answer = row.content || '';
      }
    });

    // Attach grouping/variant info
    answer.locationGroupings = locationGroupings;
    answer.locationAssignments = locationAssignments;
    answer.showLocationVariants = locationGroupings.length > 1;
    answer.formDataByGrouping = formDataByGrouping;
    answer.mainGroupingId = firstRow?.grouping_id || null;

    return answer;
  };



  // Loading animation with proper React structure
  const LoadingState = React.useCallback(() => (
    <div className="flex items-center justify-center h-full bg-gradient-to-br from-blue-50/30 via-white to-pink-50/20 relative overflow-hidden">
      <div className="text-center relative z-10">
        {/* Main Brain Icon with Sophisticated Animation */}
        <div className="relative mb-8">
        <motion.div
            className="w-24 h-24 mx-auto relative"
            initial={{ scale: 0, opacity: 0, rotateY: -180 }}
            animate={{ scale: 1, opacity: 1, rotateY: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            {/* Outer Glow Ring */}
            <motion.div
              className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-500 via-pink-500 to-yellow-400 opacity-20"
          animate={{ 
                scale: [1, 1.3, 1],
                opacity: [0.2, 0.5, 0.2],
                rotate: [0, 180, 360]
              }}
              transition={{ 
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />

            {/* Middle Ring */}
            <motion.div
              className="absolute inset-2 rounded-full bg-gradient-to-r from-pink-400 to-blue-500 opacity-30"
              animate={{ 
                scale: [1, 1.1, 1],
                opacity: [0.3, 0.6, 0.3],
                rotate: [360, 180, 0]
              }}
              transition={{ 
                duration: 2.5,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 0.5
              }}
            />

            {/* Inner Brain Icon Container */}
            <motion.div
              className="relative w-full h-full bg-gradient-to-br from-blue-500 via-pink-600 to-yellow-500 rounded-2xl shadow-2xl flex items-center justify-center overflow-hidden"
              animate={{ 
                rotateX: [0, 5, -5, 0],
                rotateY: [0, 10, -10, 0],
                boxShadow: [
                  "0 20px 60px rgba(59, 130, 246, 0.4)",
                  "0 25px 80px rgba(236, 72, 153, 0.5)",
                  "0 20px 60px rgba(255, 235, 59, 0.4)",
                  "0 20px 60px rgba(59, 130, 246, 0.4)"
                ]
              }}
              transition={{ 
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              {/* Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-black/10" />

              {/* Brain Icon */}
              <motion.div
                animate={{ 
                  scale: [1, 1.1, 1],
                  filter: [
                    "brightness(1) drop-shadow(0 0 10px rgba(255,255,255,0.3))",
                    "brightness(1.2) drop-shadow(0 0 20px rgba(255,255,255,0.5))",
                    "brightness(1) drop-shadow(0 0 10px rgba(255,255,255,0.3))"
                  ]
                }}
                transition={{ 
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              >
                <AndroidLogo className="w-12 h-12 text-white relative z-10" />
              </motion.div>
            </motion.div>
          </motion.div>
        </div>

        {/* Sequential Loading Dots with Ed Catmull Timing */}
        <div className="flex justify-center space-x-4 mb-8">
          {[0, 1, 2, 3, 4].map((index) => (
            <motion.div
              key={index}
              className="relative"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 + (index * 0.1), duration: 0.5 }}
            >
              <motion.div
                className="w-3 h-3 bg-gradient-to-r from-blue-500 via-pink-500 to-yellow-400 rounded-full relative overflow-hidden"
                animate={{ 
                  scale: [0.8, 1.3, 0.8],
                  opacity: [0.6, 1, 0.6],
                  boxShadow: [
                    "0 0 10px rgba(59, 130, 246, 0.4)",
                    "0 0 20px rgba(236, 72, 153, 0.6)",
                    "0 0 10px rgba(255, 235, 59, 0.4)"
                  ]
                }}
                transition={{
                  duration: 1.8,
                  repeat: Infinity,
                  delay: index * 0.3,
                  ease: "easeInOut"
                }}
              >
                {/* Shimmer effect */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
                  animate={{ x: [-20, 20] }}
          transition={{ 
            duration: 1.5, 
            repeat: Infinity, 
                    delay: index * 0.3,
                    ease: "easeInOut"
                  }}
                />
              </motion.div>
            </motion.div>
          ))}
        </div>

        {/* Elegant Typography with Breathing Animation */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.8 }}
          className="space-y-3"
        >
          <motion.h3 
            className="text-2xl font-bold text-gray-900 tracking-tight"
            animate={{ 
              opacity: [0.8, 1, 0.8],
              letterSpacing: ["0.025em", "0.05em", "0.025em"]
            }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          >
            Loading Brain Data
          </motion.h3>

          <motion.p 
            className="text-sm text-gray-600 font-light max-w-xs mx-auto leading-relaxed"
            animate={{ opacity: [0.7, 1, 0.7] }}
            transition={{ duration: 2.5, repeat: Infinity, delay: 0.5 }}
          >
            Initializing AI knowledge systems and neural pathways...
          </motion.p>
        </motion.div>

        {/* Sophisticated Progress Bar */}
        <motion.div
          className="mt-10 w-80 mx-auto"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 1.5, duration: 0.6 }}
        >
          <div className="h-2 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 rounded-full overflow-hidden shadow-inner">
            <motion.div
              className="h-full bg-gradient-to-r from-blue-500 via-pink-500 to-yellow-400 rounded-full relative"
              initial={{ width: "0%" }}
              animate={{ width: ["0%", "60%", "90%", "100%", "0%"] }}
              transition={{ 
                duration: 5,
                repeat: Infinity,
                ease: "easeInOut",
                times: [0, 0.3, 0.7, 0.9, 1]
              }}
            >
              {/* Progress shimmer */}
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent"
                animate={{ x: ["-100%", "100%"] }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />
            </motion.div>
      </div>

          {/* Progress percentage text */}
          <motion.div
            className="mt-3 text-xs text-gray-500 font-medium"
            animate={{ opacity: [0.6, 1, 0.6] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            Synchronizing neural networks...
          </motion.div>
        </motion.div>
      </div>

      {/* Background Floating Particles */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {[...Array(12)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-pink-400 rounded-full"
            style={{
              left: `${15 + (i * 6)}%`,
              top: `${20 + (i % 4) * 20}%`,
            }}
            animate={{
              y: [-20, -40, -20],
              x: [0, 10, 0],
              opacity: [0.1, 0.5, 0.1],
              scale: [0.5, 1.2, 0.5]
            }}
            transition={{
              duration: 4 + (i * 0.3),
              repeat: Infinity,
              delay: i * 0.5,
              ease: "easeInOut"
            }}
          />
        ))}

        {/* Additional geometric shapes */}
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={`shape-${i}`}
            className="absolute w-2 h-2 border border-yellow-300 rounded-full"
            style={{
              right: `${10 + (i * 8)}%`,
              top: `${30 + (i % 3) * 25}%`,
            }}
            animate={{
              rotate: [0, 360],
              scale: [0.5, 1, 0.5],
              opacity: [0.2, 0.4, 0.2]
            }}
            transition={{
              duration: 6 + (i * 0.4),
              repeat: Infinity,
              delay: i * 0.7,
              ease: "linear"
            }}
          />
        ))}
      </div>

      {/* Subtle background gradient animation */}
      <motion.div
        className="absolute inset-0 bg-gradient-to-br from-blue-50/20 via-transparent to-pink-50/20"
        animate={{
          opacity: [0.3, 0.6, 0.3]
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
    </div>
  ), []); // Empty dependency array ensures this only renders once

  // Empty state for when no categories exist
  const EmptyState = () => (
    <motion.div 
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden p-8 text-center"
    >
      <div className="mx-auto w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 mb-4">
        <FolderOpen className="w-6 h-6" />
      </div>
      <h3 className="text-lg font-medium text-gray-900 mb-2">No categories yet</h3>
      <p className="text-gray-500 max-w-md mx-auto mb-6">
        Get started by adding your first brain element. Categories help organize your AI's knowledge.
      </p>
      <button 
        onClick={handleAddClick}
        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-black hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black"
      >
        Add Your First Item
      </button>
    </motion.div>
  );

  // Error state
  const ErrorState = ({ message }) => (
    <div className="bg-red-50 border border-red-100 rounded-xl p-6 text-center">
      <p className="text-red-600">{message}</p>
      <button 
        onClick={fetchCategories}
        className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
      >
        Try Again
      </button>
    </div>
  );

  // Function to close all modals and reset current setting
  const resetModals = () => {
    setIsActionModalOpen(false);
    setCurrentSetting(null);
    setPreSelectedCategoryId(null); // Clear pre-selected category
    // No need to refresh - the database save already happened and UI will update naturally
  };

  // Handler for closing the OrderPathwayModal
  const handleOrderPathwayModalClose = (saved) => {
    setIsOrderPathwayModalOpen(false);
    setEditingPathwayId(null);
    if (saved) { 
      refreshBrainSettings(); // Refresh AI context
    }
  };

  // Handler for saving a new category and closing the modal
  const handleNewCategorySave = () => {
    setIsNewCategoryModalOpen(false);
    setEditingCategory(null); // Clear editing category state
    refreshBrainSettings(); // Refresh AI context
  };

  // Handler for smart upload results approval
  const handleSmartUploadResults = async (analysisResults) => {
    try {
      // Get the save results from the analysis results
      const saveData = analysisResults.saveResults;

      // Navigate to the correct nested structure: saveResults.results.results
      if (saveData && saveData.results && saveData.results.results) {
        const results = saveData.results.results;

        // Add new categories locally
        if (results.categoriesCreated && results.categoriesCreated.length > 0) {
          results.categoriesCreated.forEach((newCategory, index) => {
            // Normalize the category ID field (could be 'id' or 'category_id')
            const categoryId = newCategory?.id || newCategory?.category_id;

            // Ensure the category has a valid ID before adding to state
            if (newCategory && categoryId) {
              setCategories(prev => {
                // Check if category already exists to prevent duplicates
                const exists = prev.some(cat => cat.id === categoryId);
                if (exists) {
                  console.warn('Category already exists, skipping duplicate:', categoryId);
                  return prev;
                }

                // Normalize the data structure to always use 'id'
                return [...prev, {
                  ...newCategory,
                  id: categoryId,
                  settings: []
                }];
              });

              // Auto-expand the first newly created category
              if (index === 0) {
                setTimeout(() => {
                  setExpandedCategories(prev => new Set([...prev, categoryId]));
                }, 100);
              }
            } else {
              console.error('Invalid category data - missing ID:', newCategory);
            }
          });
        }

        // Add new settings locally
        if (results.added && results.added.length > 0) {
          results.added.forEach((newSetting, index) => {
            // Ensure the setting has proper structure for local state
            const settingForLocalState = {
              ...newSetting,
              id: newSetting.id || newSetting.setting_id,
              // Preserve both type and action_type for proper display
              type: newSetting.type,
              action_type: newSetting.action_type,
              locationVariants: []
            };
            addNewSettingToCategories(settingForLocalState);

            // Auto-select the first newly added setting for immediate editing
            if (index === 0) {
              // Find the category name for the new setting
              const category = categories.find(cat => cat.id === settingForLocalState.category_id);
              const categoryName = category?.name || 'Unknown Category';

              // Select the new setting
              setTimeout(() => {
                setSelectedItem({
                  ...settingForLocalState,
                  type: 'setting',
                  category_name: categoryName
                });

                // Expand the category if it's not already expanded
                if (settingForLocalState.category_id) {
                  setExpandedCategories(prev => new Set([...prev, settingForLocalState.category_id]));
                }
              }, 100); // Small delay to ensure state updates are processed
            }
          });
        }

        // Handle edited settings by updating them locally
        if (results.edited && results.edited.length > 0) {
          results.edited.forEach(editedSetting => {
            updateSettingInCategories(editedSetting.id, editedSetting.changes);

            // If the currently selected item was edited, refresh it with the new data
            if (selectedItem && selectedItem.id === editedSetting.id) {
              setSelectedItem(prevSelected => ({
                ...prevSelected,
                ...editedSetting.changes
              }));
            }
          });
        }

        // Handle deleted settings by removing them locally
        if (results.deleted && results.deleted.length > 0) {
          results.deleted.forEach(deletedSetting => {
            const settingId = deletedSetting.id || deletedSetting;
            removeSettingFromCategories(settingId);
          });
        }

        // Show success feedback to user
        const totalOperations = (results.categoriesCreated?.length || 0) + 
                               (results.added?.length || 0) + 
                               (results.edited?.length || 0) + 
                               (results.deleted?.length || 0);

        if (totalOperations > 0) {
          toast.success(`Successfully updated ${totalOperations} item${totalOperations !== 1 ? 's' : ''}`);
        }
      }

      // Refresh AI context (this doesn't cause UI refresh)
      refreshBrainSettings();

      // Run conflict detection to check for any overlaps with existing settings
      setTimeout(() => {
        runConflictDetectionWithAnimation();
      }, 1000);

    } catch (error) {
      console.error('Error updating brain state:', error);
      toast.error('Failed to update brain settings');
    }
  };

  // Handler for adding a new location variant
  const handleAddLocationVariant = async (settingId) => {
    try {
      // Get the current user
      const { getUserAccountId } = await import('../utils/accountUtils');
      const { accountId } = await getUserAccountId();
      if (!accountId) throw new Error('User not authenticated.');

      // Create a new grouping (location variant) for the setting
      const { data: newGrouping, error: groupingError } = await supabase
        .from('brain_groupings')
        .insert({
          settings_id: settingId,
          name: `Variant ${Date.now()}` // Simple temporary name
        })
        .select('id, name, settings_id, created_at')
        .single();

      if (groupingError) throw groupingError;

      // Refresh categories to update the sidebar with new variant
      await fetchCategories();

      // Format the new variant and select it
      const newVariant = {
        id: newGrouping.id,
        name: newGrouping.name,
        settingsId: newGrouping.settings_id,
        assignedLocationIds: [],
        created_at: newGrouping.created_at
      };

      // Find the parent setting to pass to selection
      const parentSetting = categories
        .flatMap(cat => cat.settings)
        .find(setting => setting.id === settingId);

      if (parentSetting) {
        // Select the new variant and expand the setting
        setSelectedItem({
          ...newVariant,
          parentSetting,
          type: 'variant'
        });
      }

      toast.success('Location variant created successfully');
    } catch (error) {
      console.error('Error creating location variant:', error);
      toast.error('Failed to create location variant');
    }
  };

  // Handler for opening location variants modal
  const handleOpenLocationModal = (settingId) => {
    const setting = categories
      .flatMap(cat => cat.settings)
      .find(s => s.id === settingId);

    if (setting) {
      setSelectedSettingForLocations(setting);
      setIsLocationVariantsModalOpen(true);
    }
  };

  // Handler for opening integration details modal
  const handleOpenIntegrationDetails = (integration) => {
    setSelectedIntegrationForDetails(integration);
    setIsIntegrationDetailsModalOpen(true);
  };

  // Update the onItemSelect handler to handle variants
  const handleItemSelect = (item) => {
    setSelectedItem(item);

    // If selecting a variant, make sure its parent setting is expanded
    if (item.type === 'variant' && item.parentSetting) {
      // We'll need to pass this info to the sidebar component
      // The sidebar handles its own expanded state
    }
  };

  // Accordion toggle handlers
  const toggleCategory = (categoryId) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const toggleSetting = (settingId) => {
    const newExpanded = new Set(expandedSettings);
    if (newExpanded.has(settingId)) {
      newExpanded.delete(settingId);
    } else {
      newExpanded.add(settingId);
    }
    setExpandedSettings(newExpanded);
  };

  // Integration handlers
  const handleAddIntegration = () => {
    toast.info('V2 Integration management coming soon');
  };

  // Local integration update handler for immediate UI feedback
  const handleUpdateIntegration = (integrationId, updates) => {
    setIntegrations(prevIntegrations => 
      prevIntegrations.map(integration => 
        integration.id === integrationId 
          ? { ...integration, ...updates }
          : integration
      )
    );
  };

  const handleSelectIntegration = async (provider) => {
    try {
      const { getUserAccountId } = await import('../utils/accountUtils');
      const { accountId } = await getUserAccountId();
      if (!accountId) throw new Error('User not authenticated.');

      if (provider.slug === 'curbside') {
        // Handle Curbside integration - uses curbside_settings table
        const existingCurbside = integrations.find(i => i.slug === 'curbside');

        if (existingCurbside) {
          // Just select the existing one
          setSelectedItem({
            ...existingCurbside,
            type: 'integration'
          });
          toast.success(`Selected ${provider.name} integration`);
          return;
        }

        // Check database for Curbside settings
        const { data: curbsideData, error: checkError } = await supabase
          .from('curbside_settings')
          .select('id, integration_status')
          .eq('account_id', accountId)
          .maybeSingle();

        if (checkError && checkError.code !== 'PGRST116') throw checkError;

        if (!curbsideData) {
          // Create new Curbside settings entry
          const { error: insertError } = await supabase
            .from('curbside_settings')
            .insert({
              account_id: accountId,
              integration_status: 'not_started'
            });

          if (insertError) throw insertError;
        }

        // Add to local state
        const curbsideIntegration = {
          id: 'curbside-integration',
          name: 'Curbside POS',
          integration_type: 'curbside',
          slug: 'curbside',
          status: curbsideData?.integration_status === 'complete' ? 'connected' : 'unconnected',
          is_active: curbsideData?.integration_status === 'complete'
        };

        setIntegrations(prev => [...prev, curbsideIntegration]);
        setSelectedItem({
          ...curbsideIntegration,
          type: 'integration'
        });

        toast.success(`${provider.name} integration ready to configure`);
      } else {
        // Handle regular integrations - uses accounts_integrations table
        const existingIntegration = integrations.find(i => i.slug === provider.slug);

        if (existingIntegration) {
          // Just select the existing one
          setSelectedItem({
            ...existingIntegration,
            type: 'integration'
          });
          toast.success(`Selected ${provider.name} integration`);
          return;
        }

        // Find the provider in api_providers table
        const { data: apiProvider, error: providerError } = await supabase
          .from('api_providers')
          .select('id')
          .eq('slug', provider.slug)
          .maybeSingle();

        if (providerError) {
          console.warn('Could not find provider:', providerError);
          toast.error(`${provider.name} integration setup not available yet`);
          return;
        }

        if (apiProvider) {
          // Get auth configuration for this provider
          let authMethodId = null;
          const { data: authConfig, error: authConfigError } = await supabase
            .from('auth_provider_configs')
            .select('auth_method_id')
            .eq('provider_id', apiProvider.id)
            .maybeSingle();

          if (!authConfigError && authConfig) {
            authMethodId = authConfig.auth_method_id;
          } else {
            // Fallback to 'no_auth' method if no config found
            const { data: fallbackAuth } = await supabase
              .from('auth_methods')
              .select('id')
              .eq('name', 'no_auth')
              .single();
            authMethodId = fallbackAuth?.id;
          }

          if (!authMethodId) {
            console.error('Could not determine auth method for provider');
            toast.error('Integration configuration error');
            return;
          }

          // Check if accounts_integrations entry already exists
          const { data: existingAccountIntegration, error: checkIntegrationError } = await supabase
            .from('accounts_integrations')
            .select('id, is_active')
            .eq('account_id', accountId)
            .eq('provider_id', apiProvider.id)
            .maybeSingle();

          if (checkIntegrationError) {
            console.warn('Could not check existing integration:', checkIntegrationError);
          }

          if (!existingAccountIntegration) {
            // Create entry in accounts_integrations
            const { error: insertError } = await supabase
              .from('accounts_integrations')
              .insert({
                account_id: accountId,
                provider_id: apiProvider.id,
                auth_method_id: authMethodId,
                is_active: false
              });

            if (insertError) {
              console.error('Failed to create integration entry:', insertError);
              toast.error('Failed to add integration');
              return;
            }
          }

          // Add to local state
          const newIntegration = {
            id: `${provider.slug}-integration`,
            name: provider.name,
            integration_type: provider.slug,
            slug: provider.slug,
            status: existingAccountIntegration?.is_active ? 'connected' : 'unconnected',
            is_active: existingAccountIntegration?.is_active || false
          };

          setIntegrations(prev => [...prev, newIntegration]);
          setSelectedItem({
            ...newIntegration,
            type: 'integration'
          });

          toast.success(`${provider.name} integration ready to configure`);
        } else {
          toast.error(`${provider.name} integration not found in system`);
        }
      }
    } catch (error) {
      console.error('Error adding integration:', error);
      toast.error('Failed to add integration');
    }
  };

  // Helper function to mark an operation as complete - Steve Jobs simple & elegant
  const completeLoadingOperation = (operationName) => {
    setCompletedOperations(prev => {
      const newCompleted = new Set(prev);
      newCompleted.add(operationName);

      // Only set loading to false when ALL required operations are complete
      if (REQUIRED_OPERATIONS.every(op => newCompleted.has(op))) {
        setIsLoading(false);
      }

      return newCompleted;
    });
  };

  // Unified handler for all side panels
  const handleSidePanelToggle = (panelName) => {
    // If a different panel is open, close it first, then open the new one.
    if (activeSidePanel && activeSidePanel !== panelName) {
      setActiveSidePanel(null);
      setTimeout(() => setActiveSidePanel(panelName), 300); // Wait for close animation
    } else if (activeSidePanel === panelName) {
      // If the same panel is clicked, close it.
      setActiveSidePanel(null);
    } else {
      // If no panel is open, just open the new one.
      setActiveSidePanel(panelName);
    }
  };

  // Handler for opening activation conditions panel
  const handleOpenActivationConditions = (setting) => {
    setSettingForConditions(setting);
    setIsConditionsModalOpen(true);
  };

  const handleUpdateClarificationQuestion = (updatedQuestion) => {
    setClarificationQuestions(prev => {
      // Handle removal of failed optimistic update
      if (updatedQuestion._shouldRemove) {
        return prev.filter(q => q.id !== updatedQuestion.id);
      }

      // Handle replacement of temporary ID with real database ID
      if (updatedQuestion._replaceTemp) {
        return prev.map(q => 
          q.id === updatedQuestion._replaceTemp 
            ? { ...updatedQuestion, _replaceTemp: undefined }
            : q
        );
      }

      // Handle normal update or optimistic add
      const existingIndex = prev.findIndex(q => q.id === updatedQuestion.id);
      if (existingIndex !== -1) {
        // Update existing
        return prev.map(q => q.id === updatedQuestion.id ? updatedQuestion : q);
      } else {
        // Add new (optimistic)
        return [...prev, updatedQuestion];
      }
    });
  };

  return (
    <div 
      className="h-full bg-gray-50 flex overflow-hidden"
      onWheel={(e) => e.stopPropagation()}
    >
      {/* Main Content Area - 2 Column Layout */}
      <div className="flex flex-1 h-full overflow-hidden">
        {/* Loading/Error States */}
        {isLoading ? (
          <div className="flex-1">
            <LoadingState />
          </div>
        ) : error ? (
          <div className="flex-1 p-8">
            <ErrorState message={error} />
          </div>
        ) : categories.length === 0 ? (
          <div className="flex-1 p-8">
            <EmptyState />
          </div>
        ) : (
          <>
            {/* Brain Sidebar - Column 1 */}
            {!isMobile && (
              <BrainSidebar
                categories={categories}
                integrations={integrationsV2} // Pass V2 data
                clarificationQuestions={clarificationQuestions}
                selectedItem={selectedItem}
                onItemSelect={handleItemSelect}
                onAddClick={handleAddClick}
                onEdit={handleEditCategory}
                onDelete={handleDeleteCategory}
                onDuplicate={handleDuplicateSetting}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                conflicts={conflicts}
                isLoadingConflicts={isLoadingConflicts}
                isProcessingConflicts={isProcessingConflicts}
                showConflicts={true}
                locations={locations}
                onAddLocationVariant={handleAddLocationVariant}
                onOpenLocationModal={handleOpenLocationModal}
                onAddIntegration={() => toast.info('V2 Integration management coming soon')}
                onUpdateIntegration={handleUpdateIntegration}
                onUpdateMultilingualSettings={updateMultilingualSettings}
                globalMultilingualSettings={globalMultilingualSettings}
                isMobile={false}
                expandedCategories={expandedCategories}
                expandedSettings={expandedSettings}
                onToggleCategory={toggleCategory}
                onToggleSetting={toggleSetting}
                onChatToggle={() => handleSidePanelToggle('chat')}
                isChatOpen={activeSidePanel === 'chat'}
                // Post Call Actions props
                postCallActions={postCallActions}
                onAddPostCallAction={handleAddPostCallAction}
                onPostCallActionSelect={handlePostCallActionSelect}
                expandedPostCallActions={expandedPostCallActions}
                onTogglePostCallAction={handleTogglePostCallAction}
                // AI Coverage props
                coveragePercentage={coveragePercentage}
                isLoadingCoverage={isLoadingCoverage}
                onNavigateToAnalysis={() => handleSidePanelToggle('analysis')}
                isAnalysisPanelOpen={activeSidePanel === 'analysis'}
                onToggleMixedMessagesModal={() => handleSidePanelToggle('conflicts')}
                isMixedMessagesModalOpen={activeSidePanel === 'conflicts'}
                onStartTutorial={() => setIsTutorialChaptersModalOpen(true)}
                // Test Modal props
                onToggleTestModal={() => handleSidePanelToggle('test')}
                isTestModalOpen={activeSidePanel === 'test'}
                // Affected settings filter props
                showOnlyAffectedSettings={showOnlyAffectedSettings}
                affectedSettingIds={getAffectedSettingIds()}
                // NEW: Conflict navigation props
                onOpenConflictForSetting={handleOpenConflictForSetting}
                // V2 Integration props
                integrationsV2={integrationsV2}
                isLoadingV2={isLoadingV2}
                onConnectIntegrationV2={connectIntegrationV2}
                onUpdateIntegrationCredentialsV2={updateIntegrationCredentialsV2}
                onDisconnectIntegrationV2={disconnectIntegrationV2}
                onTriggerSyncV2={triggerSyncV2}
                onToggleIntegrationStatusV2={toggleIntegrationStatusV2}
              />
            )}

            {/* Mobile Brain Sidebar */}
            <AnimatePresence>
              {isMobile && isMobileSidebarOpen && (
                <>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 bg-black/50 z-40"
                    onClick={() => setIsMobileSidebarOpen(false)}
                  />

                  <motion.div
                    initial={{ x: '100%' }}
                    animate={{ x: 0 }}
                    exit={{ x: '100%' }}
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                    className="fixed top-0 right-0 h-full w-80 max-w-[85vw] bg-white shadow-xl z-50"
                  >
                    <BrainSidebar
                      categories={categories}
                      integrations={integrationsV2} // Pass V2 data
                      clarificationQuestions={clarificationQuestions}
                      selectedItem={selectedItem}
                      onItemSelect={(item) => {
                        handleItemSelect(item);
                        setIsMobileSidebarOpen(false);
                      }}
                      onAddClick={handleAddClick}
                      onEdit={handleEditCategory}
                      onDelete={handleDeleteCategory}
                      onDuplicate={handleDuplicateSetting}
                      searchQuery={searchQuery}
                      onSearchChange={setSearchQuery}
                      conflicts={conflicts}
                      isLoadingConflicts={isLoadingConflicts}
                      isProcessingConflicts={isProcessingConflicts}
                      showConflicts={true}
                      locations={locations}
                      onAddLocationVariant={handleAddLocationVariant}
                      onOpenLocationModal={handleOpenLocationModal}
                      onAddIntegration={handleAddIntegration} // Keep legacy modal for now
                      onUpdateIntegration={handleUpdateIntegration}
                      onUpdateMultilingualSettings={updateMultilingualSettings}
                      globalMultilingualSettings={globalMultilingualSettings}
                      isMobile={true}
                      onMobileSidebarToggle={() => setIsMobileSidebarOpen(false)}
                      expandedCategories={expandedCategories}
                      expandedSettings={expandedSettings}
                      onToggleCategory={toggleCategory}
                      onToggleSetting={toggleSetting}
                      onChatToggle={() => handleSidePanelToggle('chat')}
                      isChatOpen={activeSidePanel === 'chat'}
                      // Post Call Actions props
                      postCallActions={postCallActions}
                      onAddPostCallAction={handleAddPostCallAction}
                      onPostCallActionSelect={handlePostCallActionSelect}
                      expandedPostCallActions={expandedPostCallActions}
                      onTogglePostCallAction={handleTogglePostCallAction}
                      // AI Coverage props
                      coveragePercentage={coveragePercentage}
                      isLoadingCoverage={isLoadingCoverage}
                      onNavigateToAnalysis={() => handleSidePanelToggle('analysis')}
                      isAnalysisPanelOpen={activeSidePanel === 'analysis'}
                      onToggleMixedMessagesModal={() => handleSidePanelToggle('conflicts')}
                      isMixedMessagesModalOpen={activeSidePanel === 'conflicts'}
                      onStartTutorial={() => setIsTutorialChaptersModalOpen(true)}
                      // Test Modal props
                      onToggleTestModal={() => handleSidePanelToggle('test')}
                      isTestModalOpen={activeSidePanel === 'test'}
                      // Affected settings filter props
                      showOnlyAffectedSettings={showOnlyAffectedSettings}
                      affectedSettingIds={getAffectedSettingIds()}
                      // NEW: Conflict navigation props
                      onOpenConflictForSetting={handleOpenConflictForSetting}
                      // V2 Integration props
                      integrationsV2={integrationsV2}
                      isLoadingV2={isLoadingV2}
                      onConnectIntegrationV2={connectIntegrationV2}
                      onUpdateIntegrationCredentialsV2={updateIntegrationCredentialsV2}
                      onDisconnectIntegrationV2={disconnectIntegrationV2}
                      onTriggerSyncV2={triggerSyncV2}
                      onToggleIntegrationStatusV2={toggleIntegrationStatusV2}
                    />
                  </motion.div>
                </>
              )}
            </AnimatePresence>

            {/* Main Content - Column 2 */}
            <div className={cn(
              "flex-1 overflow-hidden relative"
            )}>
              <BrainMainContent 
                selectedItem={selectedItem} 
                categories={categories}
                integrations={integrationsV2} // Pass V2 data
                clarificationQuestions={clarificationQuestions}
                onEdit={handleEditCategory}
                onDelete={handleDeleteCategory}
                onAddClick={handleAddClick}
                onUpdateSetting={updateSettingInCategories} // NEW: Enable optimistic UI updates
                onAddSetting={addNewSettingToCategories} // NEW: Enable adding new settings to categories
                onUpdateIntegration={handleUpdateIntegration} // NEW: Enable local integration updates
                onUpdateMultilingualSettings={updateMultilingualSettings} // NEW: Enable local multilingual updates
                onUpdateClarificationQuestion={(updatedQuestion) => {
                  handleUpdateClarificationQuestion(updatedQuestion);
                  if (selectedItem?.id === updatedQuestion.id) {
                    setSelectedItem(prev => ({...prev, ...updatedQuestion}));
                  }
                }}
                onDeleteClarificationQuestion={(questionId) => {
                  setClarificationQuestions(prev => prev.filter(q => q.id !== questionId));
                  if (selectedItem?.id === questionId) {
                    setSelectedItem(null);
                  }
                }}
                onOpenIntegrationDetails={handleOpenIntegrationDetails} // NEW: Handler for integration details
                onCoverageRefresh={fetchCoverageData} // Enable coverage refresh from inline modals
                onMobileSidebarToggle={() => setIsMobileSidebarOpen(true)}
                onCreateCategory={() => {
                  setCategoryCreationContext('simple_answer_modal');
                  setIsNewCategoryModalOpen(true);
                }}
                newlyCreatedCategory={newlyCreatedCategory}
                onClearNewlyCreatedCategory={() => setNewlyCreatedCategory(null)}
                locations={locations}
                isMobile={isMobile}
                isSidePanelOpen={!!activeSidePanel}
                onOpenActivationConditions={handleOpenActivationConditions}
                // V2 Integration props
                integrationsV2={integrationsV2}
                isLoadingV2={isLoadingV2}
                onConnectIntegrationV2={connectIntegrationV2}
                onUpdateIntegrationCredentialsV2={updateIntegrationCredentialsV2}
                onDisconnectIntegrationV2={disconnectIntegrationV2}
                onTriggerSyncV2={triggerSyncV2}
                onToggleIntegrationStatusV2={toggleIntegrationStatusV2}
              />
            </div>

            {/* AI Chat Interface - Column 3 */}
            <AnimatePresence>
              {activeSidePanel === 'chat' && !isMobile && (
                <motion.div
                  initial={{ width: 0, opacity: 0 }}
                  animate={{ width: 400, opacity: 1 }}
                  exit={{ width: 0, opacity: 0 }}
                  transition={{ 
                    duration: 0.3, 
                    ease: "easeInOut",
                    opacity: { duration: 0.2 }
                  }}
                  className="h-full overflow-hidden"
                >
                  <BrainChatInterface
                    isOpen={activeSidePanel === 'chat'}
                    onClose={() => setActiveSidePanel(null)}
                    onResultsApproved={handleSmartUploadResults}
                  />
                </motion.div>
              )}
            </AnimatePresence>

            {/* Mobile Chat Interface - Full Screen */}
            <AnimatePresence>
              {activeSidePanel === 'chat' && isMobile && (
                <motion.div
                  initial={{ opacity: 0, y: '100%' }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: '100%' }}
                  transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  className="fixed inset-0 z-50 bg-white"
                >
                  <BrainChatInterface
                    isOpen={activeSidePanel === 'chat'}
                    onClose={() => setActiveSidePanel(null)}
                    onResultsApproved={handleSmartUploadResults}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </>
        )}
      </div>

      {/* Mobile Floating Action Button */}
      {isMobile && !isMobileSidebarOpen && (
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setIsMobileSidebarOpen(true)}
          className="fixed bottom-6 right-6 w-14 h-14 bg-gray-100/80 text-gray-700 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center z-[20000]"
          aria-label="Open brain sidebar"
        >
          <FolderOpen className="w-6 h-6" />
        </motion.button>
      )}

      {/* Brain Action Modal */}
      <BrainActionModal 
        isOpen={isActionModalOpen} 
        onClose={() => setIsActionModalOpen(false)}
        onActionSelect={handleActionSelect}
      />



      {/* Order Pathway Modal (New) */}
      <OrderPathwayModal 
        isOpen={isOrderPathwayModalOpen}
        onClose={async (saved, newSettingData) => {
          setIsOrderPathwayModalOpen(false);
          setEditingPathwayId(null);
          setPreSelectedCategoryId(null); // Clear pre-selected category
          if (saved) { 
            // If we have new setting data, add it to local state instead of full refresh
            if (newSettingData) {
              addNewSettingToCategories(newSettingData);
              // Analyze coverage impact for the new/updated setting
              await analyzeSettingCoverage(newSettingData.id);
            } else {
              // Fallback to full refresh only if no setting data provided
              fetchCategories();
            }
            refreshBrainSettings(); // Refresh AI context
            // Update conflicts after save
            runConflictDetectionWithAnimation();
            // Update coverage display
            await fetchCoverageData();
          }
        }}
        pathwayIdToEdit={editingPathwayId}
        preSelectedCategoryId={preSelectedCategoryId}
      />

      {/* New Category Modal */}
      <NewCategoryModal 
        isOpen={isNewCategoryModalOpen}
        onClose={() => {
          setIsNewCategoryModalOpen(false);
          setEditingCategory(null); // Clear editing category state
        }}
        onSave={async (newCategoryData) => {
          setIsNewCategoryModalOpen(false);
          setEditingCategory(null); // Clear editing category state
          // If we have new category data, handle it appropriately
          if (newCategoryData) {
            if (editingCategory) {
              // Update existing category in place
              setCategories(prevCategories => 
                prevCategories.map(category => 
                  category.id === editingCategory.id 
                    ? { ...category, name: newCategoryData.name }
                    : category
                )
              );
            } else {
              // Add new category to the list
              setCategories(prevCategories => [...prevCategories, { ...newCategoryData, settings: [] }]);

              // If this was requested from SimpleAnswerModal, set it for auto-selection
              if (categoryCreationContext === 'simple_answer_modal') {
                setNewlyCreatedCategory(newCategoryData);
              }
            }
          }
          // Clear the context
          setCategoryCreationContext(null);
          refreshBrainSettings(); // Refresh AI context
          // Update conflicts after save
          runConflictDetectionWithAnimation();
          // Update coverage display
          await fetchCoverageData();
        }}
        categoryToEdit={editingCategory} // Pass the category to edit
      />

      {/* Location Variants Modal */}
      <LocationVariantsModal
        isOpen={isLocationVariantsModalOpen}
        onClose={() => {
          setIsLocationVariantsModalOpen(false);
          setSelectedSettingForLocations(null);
          // No need to refresh - the modal already saved changes to DB
        }}
        supabase={supabase}
        locations={locations}
        settings_id={selectedSettingForLocations?.id}
        setting={selectedSettingForLocations}
      />



      {/* Post Call Action Modal */}
      <PostCallActionModal
        isOpen={isPostCallActionModalOpen}
        onClose={async (saved, actionData) => {
          setIsPostCallActionModalOpen(false);
          setEditingPostCallAction(null);
          if (saved) {
            // Refresh the post call actions list
            fetchPostCallActions();
            toast.success(editingPostCallAction ? 'Post Call Action updated' : 'Post Call Action created');
            // Update conflicts after save
            runConflictDetectionWithAnimation();
            // Update coverage display
            await fetchCoverageData();
          }
        }}
        actionToEdit={editingPostCallAction}
        categories={categories}
      />

      {/* Analysis Panel - Column 3 (same as Chat Interface) */}
      <AnimatePresence>
        {activeSidePanel === 'analysis' && !isMobile && (
          <motion.div
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: 400, opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={{ 
              duration: 0.3, 
              ease: "easeInOut",
              opacity: { duration: 0.2 }
            }}
            className="h-full overflow-hidden"
          >
            <div className="w-full h-full bg-white border-l border-gray-200/50 flex flex-col">
              {/* Analysis Panel Header with Coverage Stats */}
              <motion.div 
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
                className="flex-shrink-0 border-b border-gray-200/50 bg-white/70 backdrop-blur-sm"
              >
                <div className="flex items-center justify-between p-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-50 to-blue-50 flex items-center justify-center">
                      <ChartBar className="w-4 h-4 text-indigo-600" />
                    </div>
                    <h2 className="font-light text-gray-900 text-lg tracking-tight">AI Analysis</h2>
                  </div>
                  <button
                    onClick={() => setActiveSidePanel(null)}
                    className="p-2 rounded-lg hover:bg-white/60 transition-colors text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </motion.div>

              {/* Analysis Component Container */}
              <div className="flex-1 overflow-y-auto">
                <Analysis 
                  isMobile={true} 
                  onEditSetting={handleAnalysisEditSetting}
                  onCreateSetting={handleAnalysisCreateSetting}
                />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile Analysis Panel - Full Screen */}
      <AnimatePresence>
        {activeSidePanel === 'analysis' && isMobile && (
          <motion.div
            initial={{ opacity: 0, y: '100%' }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: '100%' }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="fixed inset-0 z-50 bg-white flex flex-col"
          >
            {/* Analysis Panel Header with Coverage Stats */}
            <motion.div 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="flex-shrink-0 border-b border-gray-200/50 bg-white/70 backdrop-blur-sm"
            >
              <div className="flex items-center justify-between p-6">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-50 to-blue-50 flex items-center justify-center">
                    <ChartBar className="w-4 h-4 text-indigo-600" />
                  </div>
                  <h2 className="font-light text-gray-900 text-lg tracking-tight">AI Analysis</h2>
                </div>
                <button
                  onClick={() => setActiveSidePanel(null)}
                  className="p-2 rounded-lg hover:bg-white/60 transition-colors text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </motion.div>

            {/* Analysis Component Container */}
            <div className="flex-1 overflow-y-auto">
              <Analysis 
                isMobile={true} 
                onEditSetting={handleAnalysisEditSetting}
                onCreateSetting={handleAnalysisCreateSetting}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mixed Messages Panel - Column 3 (same as Analysis Interface) */}
      <AnimatePresence>
        {activeSidePanel === 'conflicts' && !isMobile && (
          <motion.div
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: 400, opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={{ 
              duration: 0.3, 
              ease: "easeInOut",
              opacity: { duration: 0.2 }
            }}
            className="h-full overflow-hidden"
          >
            <div className="w-full h-full bg-white border-l border-gray-200/50 flex flex-col">
              {/* Mixed Messages Component Container */}
              <div className="flex-1 overflow-y-auto">
                <MixedMessagesModal
                  isOpen={true}
                  onClose={() => setActiveSidePanel(null)}
                  conflicts={conflicts}
                  isLoadingConflicts={isLoadingConflicts}
                  isProcessingConflicts={isProcessingConflicts}
                  className="h-full border-l-0"
                  showOnlyAffectedSettings={showOnlyAffectedSettings}
                  onToggleAffectedFilter={toggleAffectedSettingsFilter}
                  onItemSelect={handleItemSelect}
                  selectedItem={selectedItem}
                  currentConflictIndex={currentConflictIndex}
                  onCurrentConflictIndexChange={setCurrentConflictIndex}
                />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile Mixed Messages Panel - Full Screen */}
      <AnimatePresence>
        {activeSidePanel === 'conflicts' && isMobile && (
          <motion.div
            initial={{ opacity: 0, y: '100%' }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: '100%' }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="fixed inset-0 z-50 bg-white flex flex-col"
          >
            {/* Mixed Messages Component Container */}
            <div className="flex-1 overflow-y-auto">
              <MixedMessagesModal
                isOpen={true}
                onClose={() => {}}
                conflicts={conflicts}
                isLoadingConflicts={isLoadingConflicts}
                isProcessingConflicts={isProcessingConflicts}
                className="h-full border-l-0"
                showOnlyAffectedSettings={showOnlyAffectedSettings}
                onToggleAffectedFilter={toggleAffectedSettingsFilter}
                onItemSelect={handleItemSelect}
                selectedItem={selectedItem}
                currentConflictIndex={currentConflictIndex}
                onCurrentConflictIndexChange={setCurrentConflictIndex}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* AI Test Modal - Column 3 (same as Analysis Interface) */}
      <AnimatePresence>
        {activeSidePanel === 'test' && !isMobile && (
          <motion.div
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: 400, opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={{ 
              duration: 0.3, 
              ease: "easeInOut",
              opacity: { duration: 0.2 }
            }}
            className="h-full overflow-hidden"
          >
            <AITestModal
              isOpen={true}
              onClose={() => setActiveSidePanel(null)}
              categories={categories}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile AI Test Modal - Full Screen */}
      <AnimatePresence>
        {activeSidePanel === 'test' && isMobile && (
          <motion.div
            initial={{ opacity: 0, y: '100%' }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: '100%' }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="fixed inset-0 z-50 bg-white flex flex-col"
          >
            <AITestModal
              isOpen={true}
              onClose={() => setActiveSidePanel(null)}
              categories={categories}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Activation Conditions Panel */}
      <AnimatePresence>
        {activeSidePanel === 'conditions' && settingForConditions && (
          <ActivationConditionsPanel
            setting={settingForConditions}
            onClose={(saved) => {
              setActiveSidePanel(null);
              setSettingForConditions(null);
              if (saved) {
                // Optionally refresh something if needed
                console.log('Activation conditions saved');
              }
            }}
          />
        )}
      </AnimatePresence>

      {/* Tutorial Chapters Modal */}
      <TutorialChaptersModal
        isOpen={isTutorialChaptersModalOpen}
        onClose={() => setIsTutorialChaptersModalOpen(false)}
        onStartChapter={handleStartTutorialChapter}
      />

      {/* Tutorial Overlay */}
      <TutorialOverlay
        isActive={isTutorialActive}
        onComplete={handleCompleteTutorialChapter}
        onSkip={handleSkipTutorialChapter}
        chapter={currentTutorialChapter}
      />

      <ActivationConditionsPanel
        isOpen={isConditionsModalOpen}
        setting={settingForConditions}
        onClose={(saved) => {
          setIsConditionsModalOpen(false);
          setSettingForConditions(null);
          if (saved) {
            console.log('Activation conditions saved');
          }
        }}
      />
    </div>
  );
};

export default Brain; 